// src/store/useProjectStore.js
import { create } from "zustand";

import {
  makeEmptyProject,
  normalizeProject,
  normalizeAtomicFacts,
  normalizeStoryArtifacts,
  INIT_ARTIFACT_KEYS } from
"../utils/normalizeProject";

/* ------------------------------------------------------------------ */
/* Utils                                                              */
/* ------------------------------------------------------------------ */

const DEBUG_AI_TAGS = true;
const ARTIFACT_KEYS = new Set(INIT_ARTIFACT_KEYS);
const UNASSIGNED_THEME_ID = "theme_unassigned";
const UNASSIGNED_THEME_TITLE = "Unassigned";
const LEGACY_WORKSPACE_PERSIST_KEY = "uxai_workspace_state_v3";
const DECISION_EVIDENCE_TYPES = new Set(["quote", "observation", "fact"]);

if (typeof window !== "undefined") {
  try {
    window.localStorage.removeItem(LEGACY_WORKSPACE_PERSIST_KEY);
  } catch {}
}

function uid(prefix = "id") {
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function now() {
  return Date.now();
}

function isoNow() {
  return new Date().toISOString();
}

function resolveUserLabel(state) {
  const user = state?.currentUser || {};
  const name = typeof user.name === "string" ? user.name.trim() : "";
  const email = typeof user.email === "string" ? user.email.trim() : "";
  const id = typeof user.id === "string" ? user.id.trim() : "";
  if (name) return name;
  if (email) return email.split("@")[0];
  return id || "local-user";
}

function safeText(v) {
  return v == null ? "" : String(v);
}

function safeArray(v) {
  return Array.isArray(v) ? v : [];
}

function normalizeStringArray(v) {
  return safeArray(v).
  map((item) => item == null ? "" : String(item).trim()).
  filter(Boolean);
}

function shouldLogDecisionEvidenceDebug() {
  try {
    return Boolean(import.meta?.env?.DEV);
  } catch {
    return false;
  }
}

function logDecisionEvidenceDebug(stage, payload) {
  if (!shouldLogDecisionEvidenceDebug()) return;
  void 0;
}

function normalizeDecisionEvidenceItems(rawEvidence) {
  const arr = Array.isArray(rawEvidence) ? rawEvidence : [];
  const seen = new Set();

  return arr.
  map((item, idx) => {
    const ev = item && typeof item === "object" ? item : {};
    const text = safeText(ev.text).trim();
    if (!text) return null;

    const typeRaw = safeText(ev.type).trim().toLowerCase();
    const type = DECISION_EVIDENCE_TYPES.has(typeRaw) ? typeRaw : "observation";
    const researchItemId = safeText(ev.researchItemId).trim() || null;
    const insightId = safeText(ev.insightId).trim() || null;

    const dedupeKey = [text, type, researchItemId || "", insightId || ""].join("::");
    if (seen.has(dedupeKey)) return null;
    seen.add(dedupeKey);

    return {
      id: safeText(ev.id).trim() || `dec_ev_${idx + 1}`,
      text,
      type,
      researchItemId,
      insightId
    };
  }).
  filter(Boolean);
}

function resolveResearchSourceName(item) {
  const candidate = safeText(
    item?.title ||
    item?.name ||
    item?.fileName ||
    item?.filename ||
    item?.originalName ||
    item?.sourceName
  ).trim();
  return candidate || safeText(item?.id).trim() || null;
}

function normalizeConfirmationEvidenceItem(rawEvidence, researchItemsById) {
  const ev = rawEvidence && typeof rawEvidence === "object" ? rawEvidence : {};
  const sourceId = safeText(ev.sourceId || ev.researchItemId).trim() || null;
  const sourceItem = sourceId && researchItemsById ? researchItemsById.get(sourceId) : null;
  const sourceType =
  safeText(ev.sourceType).trim() ||
  safeText(sourceItem?.sourceType).trim() ||
  safeText(sourceItem?.type).trim() ||
  null;
  const sourceName = safeText(ev.sourceName).trim() || resolveResearchSourceName(sourceItem);
  const excerptId = safeText(ev.excerptId || ev.id).trim() || null;
  const quote =
  safeText(ev.quote).trim() || safeText(ev.snippet).trim() || safeText(ev.text).trim() || null;

  const startCandidate = Number(ev.startOffset ?? ev.start);
  const endCandidate = Number(ev.endOffset ?? ev.end);
  const startOffset = Number.isFinite(startCandidate) ? startCandidate : null;
  const endOffset = Number.isFinite(endCandidate) ? endCandidate : null;
  const evidenceLabel = safeText(ev.evidenceLabel || ev.label).trim() || null;
  const evidenceNote = safeText(ev.evidenceNote || ev.note).trim() || null;

  if (!sourceId && !sourceName && !excerptId && !quote) return null;

  return {
    ...(sourceId ? { sourceId } : {}),
    ...(sourceType ? { sourceType } : {}),
    ...(sourceName ? { sourceName } : {}),
    ...(excerptId ? { excerptId } : {}),
    ...(quote ? { quote } : {}),
    ...(startOffset != null ? { startOffset } : {}),
    ...(endOffset != null ? { endOffset } : {}),
    ...(evidenceLabel ? { evidenceLabel } : {}),
    ...(evidenceNote ? { evidenceNote } : {})
  };
}

function normalizeDecisionConfirmationSnapshot(rawSnapshot) {
  const raw = rawSnapshot && typeof rawSnapshot === "object" ? rawSnapshot : null;
  if (!raw) return null;

  const confirmedAt = safeText(raw.confirmedAt).trim() || null;
  const confirmedBy = safeText(raw.confirmedBy).trim() || null;
  const committedInsightsVersion = Number.isFinite(Number(raw.committedInsightsVersion)) ?
  Number(raw.committedInsightsVersion) :
  null;
  const decisionTitleSnapshot = safeText(raw.decisionTitleSnapshot).trim() || "";
  const descriptionSnapshot = safeText(raw.descriptionSnapshot).trim() || "";
  const whySnapshot = safeText(raw.whySnapshot).trim() || descriptionSnapshot;

  const insightsSnapshot = safeArray(raw.insightsSnapshot).
  map((item) => {
    const insight = item && typeof item === "object" ? item : {};
    const id = safeText(insight.id).trim();
    const text = safeText(insight.text).trim();
    if (!id || !text) return null;
    const title = safeText(insight.title).trim() || deriveInsightTitleFromText(text);
    const tags = normalizeStringArray(insight.tags);
    const linkedEvidence = safeArray(insight.linkedEvidence).
    map((ev) => {
      const normalized = ev && typeof ev === "object" ? ev : {};
      const sourceId = safeText(normalized.sourceId).trim() || null;
      const sourceType = safeText(normalized.sourceType).trim() || null;
      const sourceName = safeText(normalized.sourceName).trim() || null;
      const excerptId = safeText(normalized.excerptId).trim() || null;
      const quote = safeText(normalized.quote || normalized.snippet || normalized.text).trim() || null;
      const startOffsetRaw = Number(normalized.startOffset ?? normalized.start);
      const endOffsetRaw = Number(normalized.endOffset ?? normalized.end);
      const startOffset = Number.isFinite(startOffsetRaw) ? startOffsetRaw : null;
      const endOffset = Number.isFinite(endOffsetRaw) ? endOffsetRaw : null;
      const evidenceLabel = safeText(normalized.evidenceLabel || normalized.label).trim() || null;
      const evidenceNote = safeText(normalized.evidenceNote || normalized.note).trim() || null;
      if (!sourceId && !sourceName && !excerptId && !quote) return null;
      return {
        ...(sourceId ? { sourceId } : {}),
        ...(sourceType ? { sourceType } : {}),
        ...(sourceName ? { sourceName } : {}),
        ...(excerptId ? { excerptId } : {}),
        ...(quote ? { quote } : {}),
        ...(startOffset != null ? { startOffset } : {}),
        ...(endOffset != null ? { endOffset } : {}),
        ...(evidenceLabel ? { evidenceLabel } : {}),
        ...(evidenceNote ? { evidenceNote } : {})
      };
    }).
    filter(Boolean);

    return {
      id,
      title,
      text,
      ...(tags.length ? { tags } : {}),
      linkedEvidence
    };
  }).
  filter(Boolean);

  return {
    ...(confirmedAt ? { confirmedAt } : {}),
    ...(confirmedBy ? { confirmedBy } : {}),
    committedInsightsVersion,
    decisionTitleSnapshot,
    descriptionSnapshot,
    whySnapshot,
    insightsSnapshot
  };
}

function buildDecisionConfirmationSnapshot({ decision, currentInit, confirmedAt, confirmedBy }) {
  const approvedInsights = safeArray(currentInit?.insightsApprovedSnapshot?.approvedInsights);
  const liveInsights = safeArray(currentInit?.researchInsights?.items);
  const insightsById = new Map(
    [...approvedInsights, ...liveInsights].
    map((insight) => [safeText(insight?.id).trim(), insight]).
    filter(([id]) => Boolean(id))
  );
  const researchItemsById = new Map(
    safeArray(currentInit?.researchItems).map((item) => [safeText(item?.id).trim(), item])
  );
  const linkedInsightIds = normalizeStringArray(decision?.linkedInsightIds);
  const committedInsightsVersion = Number.isFinite(Number(currentInit?.insightsVersion)) ?
  Number(currentInit.insightsVersion) :
  null;

  const insightsSnapshot = linkedInsightIds.
  map((insightId) => insightsById.get(insightId)).
  filter(Boolean).
  map((insight) => {
    const text = safeText(insight?.text).trim();
    const title = safeText(insight?.title).trim() || deriveInsightTitleFromText(text);
    const tags = normalizeStringArray(insight?.tags);
    const linkedEvidence = safeArray(insight?.evidence).
    map((ev) => normalizeConfirmationEvidenceItem(ev, researchItemsById)).
    filter(Boolean);
    return {
      id: safeText(insight?.id).trim(),
      title,
      text,
      ...(tags.length ? { tags } : {}),
      linkedEvidence
    };
  }).
  filter((insight) => insight.id && insight.text);

  return {
    confirmedAt: safeText(confirmedAt).trim() || isoNow(),
    confirmedBy: safeText(confirmedBy).trim() || "local-user",
    committedInsightsVersion,
    decisionTitleSnapshot: safeText(decision?.title).trim(),
    descriptionSnapshot: safeText(decision?.description).trim(),
    whySnapshot: safeText(decision?.description).trim(),
    insightsSnapshot
  };
}

function normalizeDocText(s) {
  return safeText(s).replace(/\r\n/g, "\n").replace(/\r/g, "\n");
}

function safeLowerTag(v) {
  return safeText(v).trim().toLowerCase().replace(/\s+/g, " ");
}

function normalizeManualMappingState(raw) {
  const obj = raw && typeof raw === "object" ? raw : {};
  const draftGroupsRaw = Array.isArray(obj.draftGroups) ? obj.draftGroups : [];
  const draftGroups = draftGroupsRaw.
  map((g, idx) => {
    if (!g || typeof g !== "object") return null;
    const id = typeof g.id === "string" && g.id.trim() ? g.id.trim() : `mm_grp_${idx + 1}`;
    const title = typeof g.title === "string" && g.title.trim() ? g.title.trim() : `Group ${idx + 1}`;
    const atomicFactIds = Array.isArray(g.atomicFactIds) ?
    Array.from(
      new Set(g.atomicFactIds.map((id) => typeof id === "string" ? id.trim() : "").filter(Boolean))
    ) :
    [];
    return { id, title, atomicFactIds };
  }).
  filter(Boolean);

  const unassignedAtomicFactIds = Array.isArray(obj.unassignedAtomicFactIds) ?
  Array.from(
    new Set(
      obj.unassignedAtomicFactIds.map((id) => typeof id === "string" ? id.trim() : "").filter(Boolean)
    )
  ) :
  [];

  return {
    isActive: obj.isActive === true,
    draftGroups,
    unassignedAtomicFactIds,
    startedAt: typeof obj.startedAt === "string" && obj.startedAt.trim() ? obj.startedAt.trim() : null,
    lastEditedAt: typeof obj.lastEditedAt === "string" && obj.lastEditedAt.trim() ? obj.lastEditedAt.trim() : null
  };
}

function normalizeVersion(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function getResearchVersion(init) {
  const items = Array.isArray(init?.researchItems) ? init.researchItems : [];
  return normalizeVersion(init?.researchVersion, items.length > 0 ? 1 : 0);
}

function bumpVersion(value, fallback = 0) {
  const base = normalizeVersion(value, fallback);
  return base + 1;
}

function markInsightsDerived(nextInit, researchVersion) {
  return {
    ...nextInit,
    insightsMeta: {
      ...(nextInit?.insightsMeta && typeof nextInit.insightsMeta === "object" ? nextInit.insightsMeta : {}),
      derivedFromResearchVersion: researchVersion
    }
  };
}

function markTagsDerived(nextInit, researchVersion) {
  return {
    ...nextInit,
    tagsVersion: bumpVersion(nextInit?.tagsVersion, 0),
    tagsMeta: {
      ...(nextInit?.tagsMeta && typeof nextInit.tagsMeta === "object" ? nextInit.tagsMeta : {}),
      derivedFromResearchVersion: researchVersion
    }
  };
}

const RESEARCH_METHODS = new Set([
"user_feedback",
"interview_notes",
"usability_testing",
"survey_results",
"analytics_data",
"research_materials",
"other"]
);

const RESEARCH_SOURCE_TYPES = new Set([
"document",
"screenshot",
"structured",
"paste"]
);

const DEFAULT_METHOD_BY_TYPE = {
  interviews: "interview_notes",
  messages: "user_feedback",
  tickets: "user_feedback",
  other: "other"
};

const RESEARCH_ANALYSIS_STATUSES = new Set(["ready", "analyzing", "analyzed", "failed"]);
const RESEARCH_ANALYSIS_ERROR_CODES = new Set(["text_too_long", "temporary_unavailable", "processing_failed"]);

function normalizeResearchMethod(method, type) {
  const raw = typeof method === "string" ? method.trim() : "";
  if (RESEARCH_METHODS.has(raw)) return raw;
  const typeKey = typeof type === "string" ? type.trim().toLowerCase() : "";
  return DEFAULT_METHOD_BY_TYPE[typeKey] || "other";
}

function normalizeResearchSourceType(sourceType) {
  const raw = typeof sourceType === "string" ? sourceType.trim() : "";
  if (RESEARCH_SOURCE_TYPES.has(raw)) return raw;
  return "paste";
}

function normalizeResearchItems(items) {
  const arr = Array.isArray(items) ? items : [];
  return arr.map((item, idx) => {
    const it = item && typeof item === "object" ? item : {};
    const t = now();
    const rawText =
    typeof it.rawText === "string" ?
    it.rawText :
    typeof it.text === "string" ?
    it.text :
    "";
    const normalizedText = normalizeDocText(rawText);
    const rawType =
    typeof it.type === "string" ? it.type.trim().toLowerCase() : "";
    const type = rawType || "other";
    const method = normalizeResearchMethod(it.method, type);
    const sourceType = normalizeResearchSourceType(it.sourceType);
    const analysisStatusRaw =
    typeof it.analysisStatus === "string" ? it.analysisStatus.trim().toLowerCase() : "";
    const analysisStatus = RESEARCH_ANALYSIS_STATUSES.has(analysisStatusRaw) ?
    analysisStatusRaw :
    "";
    const analysisErrorCodeRaw =
    typeof it.analysisErrorCode === "string" ? it.analysisErrorCode.trim().toLowerCase() : "";
    const analysisErrorCode = RESEARCH_ANALYSIS_ERROR_CODES.has(analysisErrorCodeRaw) ?
    analysisErrorCodeRaw :
    "";
    const analysisErrorReason =
    typeof it.analysisErrorReason === "string" ? it.analysisErrorReason.trim() : "";
    const analyzedAt = Number.isFinite(Number(it.analyzedAt)) ? Number(it.analyzedAt) : null;
    const analysisUpdatedAt = Number.isFinite(Number(it.analysisUpdatedAt)) ?
    Number(it.analysisUpdatedAt) :
    t;

    return {
      ...it,
      id:
      typeof it.id === "string" && it.id.trim() ?
      it.id.trim() :
      `ri_${idx + 1}`,
      type,
      method,
      sourceType,
      rawText,
      normalizedText,
      analysisStatus,
      analysisErrorCode: analysisStatus === "failed" ? analysisErrorCode : "",
      analysisErrorReason: analysisStatus === "failed" ? analysisErrorReason : "",
      analysisRetryable: analysisStatus === "failed" ? it.analysisRetryable === true : false,
      analyzedAt,
      analysisUpdatedAt,
      createdAt: Number.isFinite(Number(it.createdAt)) ?
      Number(it.createdAt) :
      t,
      updatedAt: Number.isFinite(Number(it.updatedAt)) ?
      Number(it.updatedAt) :
      t
    };
  });
}

function normalizeResearchTagSpans(container) {
  const t = now();
  const raw = container && typeof container === "object" ? container : null;
  const itemsRaw = Array.isArray(raw?.items) ? raw.items : [];

  const items = itemsRaw.
  map((x, idx) => {
    const o = x && typeof x === "object" ? x : null;
    if (!o) return null;

    const tag = safeLowerTag(o.tag);
    if (!tag) return null;

    const researchItemId = safeText(o.researchItemId).trim();
    if (!researchItemId) return null;

    const start = Number(o.start);
    const end = Number(o.end);
    if (
    !Number.isFinite(start) ||
    !Number.isFinite(end) ||
    start < 0 ||
    end <= start)

    return null;

    const id =
    safeText(o.id).trim() || `tag_${researchItemId}_${start}_${end}_${idx}`;
    const context = typeof o.context === "string" ? o.context : undefined;
    const createdAt = Number.isFinite(Number(o.createdAt)) ?
    Number(o.createdAt) :
    t;
    const updatedAt = Number.isFinite(Number(o.updatedAt)) ?
    Number(o.updatedAt) :
    createdAt;

    return {
      id,
      tag,
      researchItemId,
      start,
      end,
      ...(context ? { context } : {}),
      createdAt,
      updatedAt
    };
  }).
  filter(Boolean);

  const seen = new Set();
  const deduped = [];
  for (const it of items) {
    const k = `${it.tag}::${it.researchItemId}::${it.start}::${it.end}`;
    if (seen.has(k)) continue;
    seen.add(k);
    deduped.push(it);
  }

  return {
    version: Number.isFinite(Number(raw?.version)) ? Number(raw.version) : 1,
    items: deduped,
    createdAt: Number.isFinite(Number(raw?.createdAt)) ?
    Number(raw.createdAt) :
    t,
    updatedAt: Number.isFinite(Number(raw?.updatedAt)) ?
    Number(raw.updatedAt) :
    t
  };
}

function normalizeTagVocabulary(container) {
  const t = now();
  const raw = container && typeof container === "object" ? container : null;
  const itemsRaw = Array.isArray(raw?.items) ?
  raw.items :
  Array.isArray(raw?.tags) ?
  raw.tags :
  [];

  const items = [];
  const seen = new Set();

  for (const x of itemsRaw) {
    const rawTag =
    typeof x === "string" ?
    x :
    x && typeof x === "object" ?
    x.tag ?? x.name ?? x.label ?? "" :
    "";
    const tag = safeLowerTag(rawTag);
    if (!tag || seen.has(tag)) continue;
    seen.add(tag);

    const description =
    x && typeof x === "object" ? safeText(x.description ?? x.desc).trim() : "";
    items.push({ tag, description });
  }

  return {
    version: Number.isFinite(Number(raw?.version)) ? Number(raw.version) : 1,
    items,
    createdAt: Number.isFinite(Number(raw?.createdAt)) ? Number(raw.createdAt) : t,
    updatedAt: Number.isFinite(Number(raw?.updatedAt)) ? Number(raw.updatedAt) : t
  };
}

function normalizeTagThemes(container) {
  const t = now();
  const raw = container && typeof container === "object" ? container : null;
  const themesRaw = Array.isArray(raw?.themes) ? raw.themes : [];
  const overridesRaw = raw?.overrides && typeof raw.overrides === "object" ? raw.overrides : {};

  const themes = themesRaw.
  map((theme, idx) => {
    if (!theme || typeof theme !== "object") return null;
    const title = safeText(theme.title ?? theme.name).trim() || `Theme ${idx + 1}`;
    const rawId = safeText(theme.id).trim();
    const id = rawId || `theme_${title.toLowerCase().replace(/[^a-z0-9]+/g, "_")}`;
    const order = Number.isFinite(Number(theme.order)) ? Number(theme.order) : idx;
    const origin = theme.origin === "user" ? "user" : theme.origin === "system" ? "system" : "ai";

    const tagsRaw = Array.isArray(theme.tags) ?
    theme.tags :
    typeof theme.tags === "string" ?
    theme.tags.split(",") :
    [];

    const tags = [];
    const seen = new Set();
    for (const tg of tagsRaw) {
      const tag = safeLowerTag(tg);
      if (!tag || seen.has(tag)) continue;
      seen.add(tag);
      tags.push(tag);
    }

    const createdAt = Number.isFinite(Number(theme.createdAt)) ? Number(theme.createdAt) : t;
    const updatedAt = Number.isFinite(Number(theme.updatedAt)) ? Number(theme.updatedAt) : createdAt;

    return { id, title, order, tags, origin, createdAt, updatedAt };
  }).
  filter(Boolean).
  sort((a, b) => a.order - b.order);

  const tagToThemeIdRaw =
  overridesRaw.tagToThemeId && typeof overridesRaw.tagToThemeId === "object" ?
  overridesRaw.tagToThemeId :
  {};
  const themeTitlesRaw =
  overridesRaw.themeTitles && typeof overridesRaw.themeTitles === "object" ?
  overridesRaw.themeTitles :
  {};

  const tagToThemeId = {};
  Object.entries(tagToThemeIdRaw).forEach(([tag, themeId]) => {
    const tKey = safeLowerTag(tag);
    const tVal = safeText(themeId).trim();
    if (tKey && tVal) tagToThemeId[tKey] = tVal;
  });

  const themeTitles = {};
  Object.entries(themeTitlesRaw).forEach(([themeId, title]) => {
    const id = safeText(themeId).trim();
    const tTitle = safeText(title).trim();
    if (id && tTitle) themeTitles[id] = tTitle;
  });

  return {
    version: Number.isFinite(Number(raw?.version)) ? Number(raw.version) : 1,
    themes,
    overrides: { tagToThemeId, themeTitles },
    createdAt: Number.isFinite(Number(raw?.createdAt)) ? Number(raw.createdAt) : t,
    updatedAt: Number.isFinite(Number(raw?.updatedAt)) ? Number(raw.updatedAt) : t
  };
}

function applyTagThemeInvariants({
  tagVocabulary,
  tagThemes,
  researchTagSpans,
  researchInsights,
  includeThemeTags = true,
  includeThemeOverrides = true
}) {
  const t = now();
  const normalizedVocab = normalizeTagVocabulary(tagVocabulary);
  const normalizedThemes = normalizeTagThemes(tagThemes);

  const knownTags = collectKnownTags({
    tagVocabulary: normalizedVocab,
    researchTagSpans,
    researchInsights,
    tagThemes: normalizedThemes,
    includeThemeTags,
    includeThemeOverrides
  });

  const vocabMap = new Map(
    (normalizedVocab.items || []).map((it) => [it.tag, it])
  );
  const vocabItems = [...(normalizedVocab.items || [])];
  knownTags.forEach((tag) => {
    if (!vocabMap.has(tag)) {
      vocabItems.push({ tag, description: "" });
      vocabMap.set(tag, { tag, description: "" });
    }
  });

  const vocabSet = new Set(vocabItems.map((it) => it.tag));
  const themeIds = new Set(normalizedThemes.themes.map((th) => th.id));
  const seenTags = new Set();

  const themes = normalizedThemes.themes.map((theme) => {
    const tags = [];
    (Array.isArray(theme.tags) ? theme.tags : []).forEach((tag) => {
      if (!vocabSet.has(tag)) return;
      if (seenTags.has(tag)) return;
      seenTags.add(tag);
      tags.push(tag);
    });
    return { ...theme, tags };
  });

  const nextTagToThemeId = {};
  Object.entries(normalizedThemes.overrides?.tagToThemeId || {}).forEach(
    ([tag, themeId]) => {
      const tKey = safeLowerTag(tag);
      const tVal = safeText(themeId).trim();
      if (!tKey || !tVal) return;
      if (!vocabSet.has(tKey)) return;
      if (tVal !== UNASSIGNED_THEME_ID && !themeIds.has(tVal)) return;
      nextTagToThemeId[tKey] = tVal;
    }
  );

  const assignedTags = new Set(seenTags);
  Object.keys(nextTagToThemeId).forEach((tag) => assignedTags.add(tag));

  // Keep every known tag assigned to a single theme (unassigned fallback).
  vocabSet.forEach((tag) => {
    if (!assignedTags.has(tag)) {
      nextTagToThemeId[tag] = UNASSIGNED_THEME_ID;
    }
  });

  return {
    tagVocabulary: { ...normalizedVocab, items: vocabItems, updatedAt: t },
    tagThemes: {
      ...normalizedThemes,
      themes,
      overrides: { ...normalizedThemes.overrides, tagToThemeId: nextTagToThemeId },
      updatedAt: t
    }
  };
}

function collectKnownTags({
  tagVocabulary,
  researchTagSpans,
  researchInsights,
  tagThemes,
  includeThemeTags = true,
  includeThemeOverrides = true
}) {
  const tags = new Set();
  const vocabItems = Array.isArray(tagVocabulary?.items) ? tagVocabulary.items : [];
  vocabItems.forEach((it) => {
    const tag = safeLowerTag(it?.tag);
    if (tag) tags.add(tag);
  });

  const spanItems = Array.isArray(researchTagSpans?.items) ? researchTagSpans.items : [];
  spanItems.forEach((it) => {
    const tag = safeLowerTag(it?.tag);
    if (tag) tags.add(tag);
  });

  const insightItems = Array.isArray(researchInsights?.items) ? researchInsights.items : [];
  insightItems.forEach((it) => {
    const arr = Array.isArray(it?.tags) ? it.tags : [];
    arr.forEach((tag) => {
      const t = safeLowerTag(tag);
      if (t) tags.add(t);
    });
  });

  if (includeThemeTags) {
    const themeItems = Array.isArray(tagThemes?.themes) ? tagThemes.themes : [];
    themeItems.forEach((th) => {
      const arr = Array.isArray(th?.tags) ? th.tags : [];
      arr.forEach((tag) => {
        const t = safeLowerTag(tag);
        if (t) tags.add(t);
      });
    });
  }

  if (includeThemeOverrides) {
    const overrides = tagThemes?.overrides?.tagToThemeId || {};
    Object.keys(overrides).forEach((tag) => {
      const t = safeLowerTag(tag);
      if (t) tags.add(t);
    });
  }

  return tags;
}

function toTimestamp(value) {
  const n = Number(value);
  if (Number.isFinite(n) && n > 0) return n;
  if (typeof value === "string" && value.trim()) {
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return 0;
}

function initiativeUpdatedAt(initiative) {
  if (!initiative || typeof initiative !== "object") return 0;
  return Math.max(
    toTimestamp(initiative?.updatedAt),
    toTimestamp(initiative?.meta?.updatedAt),
    0
  );
}

function mergeInitiativesByTimestamp(remoteInitiatives, localInitiatives) {
  const remoteAllIds = Array.isArray(remoteInitiatives?.allIds) ? remoteInitiatives.allIds : [];
  const localAllIds = Array.isArray(localInitiatives?.allIds) ? localInitiatives.allIds : [];
  const remoteById =
  remoteInitiatives?.byId && typeof remoteInitiatives.byId === "object" ? remoteInitiatives.byId : {};
  const localById =
  localInitiatives?.byId && typeof localInitiatives.byId === "object" ? localInitiatives.byId : {};

  const allIds = Array.from(new Set([...remoteAllIds, ...localAllIds]));
  const byId = {};

  allIds.forEach((id) => {
    const remoteInit = remoteById[id];
    const localInit = localById[id];
    if (!remoteInit && localInit) {
      byId[id] = localInit;
      return;
    }
    if (remoteInit && !localInit) {
      byId[id] = remoteInit;
      return;
    }
    if (!remoteInit && !localInit) return;

    const remoteTs = initiativeUpdatedAt(remoteInit);
    const localTs = initiativeUpdatedAt(localInit);
    byId[id] = localTs >= remoteTs ? localInit : remoteInit;
  });

  const activeId =
  remoteInitiatives && remoteInitiatives.activeId && byId[remoteInitiatives.activeId] && remoteInitiatives.activeId ||
  localInitiatives && localInitiatives.activeId && byId[localInitiatives.activeId] && localInitiatives.activeId ||
  allIds[0] ||
  null;

  return { allIds, byId, activeId };
}

function mergeProjectSnapshotsByTimestamp(remoteSnapshot, localSnapshot) {
  if (!remoteSnapshot || typeof remoteSnapshot !== "object") return localSnapshot;
  if (!localSnapshot || typeof localSnapshot !== "object") return remoteSnapshot;

  const merged = {
    ...remoteSnapshot,
    ...localSnapshot
  };

  merged.meta = {
    ...(remoteSnapshot.meta || {}),
    ...(localSnapshot.meta || {})
  };

  const remoteProjectTs = Math.max(
    toTimestamp(remoteSnapshot?.updatedAt),
    toTimestamp(remoteSnapshot?.meta?.updatedAt),
    0
  );
  const localProjectTs = Math.max(
    toTimestamp(localSnapshot?.updatedAt),
    toTimestamp(localSnapshot?.meta?.updatedAt),
    0
  );
  const effectiveProjectTs = Math.max(remoteProjectTs, localProjectTs);
  if (effectiveProjectTs) {
    merged.updatedAt = effectiveProjectTs;
    merged.meta.updatedAt = effectiveProjectTs;
  }

  merged.initiatives = mergeInitiativesByTimestamp(
    remoteSnapshot?.initiatives,
    localSnapshot?.initiatives
  );

  return merged;
}

function buildEffectiveTagThemes(rawTagThemes, knownTags) {
  const t = now();
  const normalized = normalizeTagThemes(rawTagThemes);
  const themeById = new Map(normalized.themes.map((th) => [th.id, { ...th }]));
  const tagToTheme = {};

  normalized.themes.forEach((theme) => {
    theme.tags.forEach((tag) => {
      if (!tagToTheme[tag]) tagToTheme[tag] = theme.id;
    });
  });

  const titleOverrides = normalized.overrides?.themeTitles || {};
  Object.entries(titleOverrides).forEach(([themeId, title]) => {
    const theme = themeById.get(themeId);
    if (theme && title) theme.title = title;
  });

  const tagOverrides = normalized.overrides?.tagToThemeId || {};
  Object.entries(tagOverrides).forEach(([tag, themeId]) => {
    if (!themeId) return;
    if (!themeById.has(themeId) && themeId !== UNASSIGNED_THEME_ID) return;
    tagToTheme[tag] = themeId;
  });

  const unassignedTags = [];
  const tagList = Array.from(knownTags || []);
  for (const tag of tagList) {
    const assigned = tagToTheme[tag];
    if (!assigned) {
      unassignedTags.push(tag);
      tagToTheme[tag] = UNASSIGNED_THEME_ID;
    }
  }

  const effectiveThemes = [];
  const grouped = new Map();

  Object.entries(tagToTheme).forEach(([tag, themeId]) => {
    if (!grouped.has(themeId)) grouped.set(themeId, []);
    grouped.get(themeId).push(tag);
  });

  themeById.forEach((theme) => {
    const tags = grouped.get(theme.id) || [];
    effectiveThemes.push({
      ...theme,
      tags: tags.sort((a, b) => a.localeCompare(b))
    });
  });

  if (unassignedTags.length) {
    effectiveThemes.push({
      id: UNASSIGNED_THEME_ID,
      title: UNASSIGNED_THEME_TITLE,
      order: effectiveThemes.length,
      tags: unassignedTags.sort((a, b) => a.localeCompare(b)),
      origin: "system",
      createdAt: t,
      updatedAt: t
    });
  }

  return {
    ...normalized,
    themes: effectiveThemes.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    updatedAt: t
  };
}

function mergeTagThemesWithAI(currentTagThemes, incomingTagThemes) {
  const current = normalizeTagThemes(currentTagThemes);
  const incoming = normalizeTagThemes(incomingTagThemes);

  const userThemes = current.themes.filter((t) => t.origin === "user");
  const aiThemes = incoming.themes.map((t) => ({ ...t, origin: "ai" }));

  const mergedThemes = [...aiThemes, ...userThemes].map((theme, idx) => ({
    ...theme,
    order: idx
  }));

  return {
    ...incoming,
    themes: mergedThemes,
    overrides: {
      tagToThemeId: {},
      themeTitles: current.overrides?.themeTitles || {}
    },
    createdAt: current.createdAt ?? incoming.createdAt ?? now(),
    updatedAt: now()
  };
}

/* ------------------------------------------------------------------ */
/* Insights helpers                                                   */
/* ------------------------------------------------------------------ */

function locateEvidenceOffsets(evidenceText, normalizedText) {
  const text = safeText(evidenceText);
  const full = safeText(normalizedText);
  if (!text || !full) return null;

  // Try exact match first
  let at = full.indexOf(text);
  if (at !== -1) return { start: at, end: at + text.length };

  // Fallback: match on normalized whitespace (keeps offsets unreliable -> skip)
  // We deliberately do NOT attempt fuzzy match because offsets must be precise.
  return null;
}

function normalizeEvidenceItems(raw, researchItemsById) {
  const arr = Array.isArray(raw) ? raw : [];
  const seen = new Set();
  return arr.
  map((e, idx) => {
    const o = e && typeof e === "object" ? e : null;
    if (!o) return null;

    const text = typeof o.text === "string" ? o.text.trim() : "";
    if (!text) return null;

    const typeRaw = typeof o.type === "string" ? o.type.trim() : "";
    const type =
    typeRaw === "quote" || typeRaw === "observation" || typeRaw === "fact" ?
    typeRaw :
    "observation";

    const researchItemId =
    typeof o.researchItemId === "string" && o.researchItemId.trim() ?
    o.researchItemId.trim() :
    null;

    let start = Number.isFinite(Number(o.start)) ? Number(o.start) : undefined;
    let end = Number.isFinite(Number(o.end)) ? Number(o.end) : undefined;

    if (
    researchItemId &&
    researchItemsById && (
    !Number.isFinite(start) || !Number.isFinite(end)))
    {
      const doc = researchItemsById.get(researchItemId);
      const offsets = locateEvidenceOffsets(text, doc?.normalizedText || "");
      if (offsets) {
        start = offsets.start;
        end = offsets.end;
      }
    }

    const key = [
    text,
    type,
    researchItemId || "",
    Number.isFinite(start) ? String(start) : "",
    Number.isFinite(end) ? String(end) : ""].

    filter(Boolean).
    join("::");
    if (key && seen.has(key)) return null;
    if (key) seen.add(key);

    const id =
    typeof o.id === "string" && o.id.trim() ? o.id.trim() : `ev_${key || idx + 1}`;

    return {
      id,
      text,
      type,
      researchItemId,
      ...(Number.isFinite(start) ? { start } : {}),
      ...(Number.isFinite(end) ? { end } : {})
    };
  }).
  filter(Boolean);
}

function computeEvidenceStrength(evidence) {
  const arr = Array.isArray(evidence) ? evidence : [];
  const count = arr.length;
  if (!count) return "none";
  const sources = new Set(arr.map((e) => e?.researchItemId).filter(Boolean));
  const sourceCount = sources.size;
  if (sourceCount >= 3 || count >= 4) return "high";
  if (sourceCount >= 2 || count >= 2 && count <= 3) return "medium";
  return "low";
}

function computeNeedsEvidence(evidence) {
  const strength = computeEvidenceStrength(evidence);
  return strength === "none" || strength === "low";
}

function filterEvidenceForResearch(evidence, researchItemCount) {
  const arr = Array.isArray(evidence) ? evidence : [];
  if (researchItemCount > 0) return arr.filter((e) => e?.researchItemId);
  return arr;
}

function deriveInsightTitleFromText(text, { maxWords = 8, maxChars = 80 } = {}) {
  const raw = safeText(text).replace(/[“”"]/g, "").trim();
  if (!raw) return "Untitled insight";
  const sentence = raw.split(/(?<=[.!?])\s+/)[0] || raw;
  const words = sentence.split(/\s+/).filter(Boolean).slice(0, maxWords);
  let title = words.join(" ");
  if (title.length > maxChars) title = title.slice(0, maxChars).trim();
  return title || "Untitled insight";
}

function normalizeInsightItem(raw, idx, researchItemCount, researchItemsById) {
  const t = now();
  const it = raw && typeof raw === "object" ? raw : {};
  const text = typeof it.text === "string" ? it.text.trim() : "";
  const titleRaw = typeof it.title === "string" ? it.title.trim() : "";
  const title = titleRaw || deriveInsightTitleFromText(text);
  if (!text) return null;
  const sourceTypeRaw =
  typeof it.sourceType === "string" ?
  it.sourceType.trim().toLowerCase() :
  typeof it.source?.type === "string" ?
  it.source.type.trim().toLowerCase() :
  "";
  const sourceType = ["research", "prompt", "ai_suggested"].includes(sourceTypeRaw) ?
  sourceTypeRaw :
  undefined;
  const sourceRaw =
  typeof it.source === "string" ?
  it.source.trim().toLowerCase() :
  typeof it.source?.type === "string" ?
  it.source.type.trim().toLowerCase() :
  "";
  const source = sourceRaw === "hypothesis" ? "hypothesis" : undefined;

  const tags = Array.isArray(it.tags) ?
  it.tags.
  filter((x) => typeof x === "string" && x.trim()).
  map((x) => x.trim()) :
  [];

  const groupId =
  typeof it.groupId === "string" && it.groupId.trim() ?
  it.groupId.trim() :
  "grp_ungrouped";

  const reviewStatusRaw =
  typeof it.reviewStatus === "string" ? it.reviewStatus : "ai_generated";
  const reviewStatus =
  reviewStatusRaw === "reviewed" ||
  reviewStatusRaw === "approved" ||
  reviewStatusRaw === "edited" ?
  reviewStatusRaw :
  "ai_generated";

  const provenanceRaw =
  it.provenance && typeof it.provenance === "object" ? it.provenance : {};
  const createdAt = Number.isFinite(Number(provenanceRaw.createdAt)) ?
  Number(provenanceRaw.createdAt) :
  t;
  const updatedAt = Number.isFinite(Number(provenanceRaw.updatedAt)) ?
  Number(provenanceRaw.updatedAt) :
  createdAt;

  const provenance = {
    createdBy: provenanceRaw.createdBy === "human" ? "human" : "ai",
    lastEditedBy:
    provenanceRaw.lastEditedBy === "human" ?
    "human" :
    provenanceRaw.lastEditedBy === "ai" ?
    "ai" :
    "ai",
    createdAt,
    updatedAt,
    ...(provenanceRaw.modelRunId ? { modelRunId: provenanceRaw.modelRunId } : {})
  };

  const fallbackCreatedBy = provenance?.createdBy === "ai" ? "ai" : "local-user";
  const createdBy =
  typeof it.createdBy === "string" && it.createdBy.trim() ?
  it.createdBy.trim() :
  fallbackCreatedBy;
  const updatedBy =
  typeof it.updatedBy === "string" && it.updatedBy.trim() ?
  it.updatedBy.trim() :
  createdBy;
  const updatedAtIso =
  typeof it.updatedAt === "string" && it.updatedAt.trim() ?
  it.updatedAt.trim() :
  null;

  const rawEvidence = normalizeEvidenceItems(it.evidence, researchItemsById);
  const evidence = filterEvidenceForResearch(rawEvidence, researchItemCount);
  const evidenceStrength = computeEvidenceStrength(evidence);

  const needsEvidence =
  typeof it.needsEvidence === "boolean" ?
  it.needsEvidence :
  computeNeedsEvidence(evidence);
  const factIds = Array.isArray(it.factIds) ?
  Array.from(
    new Set(
      it.factIds.
      map((id) => safeText(id).trim()).
      filter(Boolean)
    )
  ) :
  [];

  return {
    id:
    typeof it.id === "string" && it.id.trim() ? it.id.trim() : `ins_${idx + 1}`,
    title,
    text,
    ...(tags.length ? { tags } : {}),
    ...(groupId ? { groupId } : {}),
    ...(Number.isFinite(Number(it.order)) ?
    { order: Number(it.order) } :
    { order: idx }),
    reviewStatus,
    provenance,
    createdBy,
    updatedBy,
    ...(updatedAtIso ? { updatedAt: updatedAtIso } : {}),
    needsEvidence,
    evidenceStrength,
    evidence,
    ...(factIds.length ? { factIds } : {}),
    ...(sourceType ? { sourceType } : {}),
    ...(source ? { source } : {})
  };
}

function normalizeInsightsContainer(container, researchItems) {
  const t = now();
  const raw = container && typeof container === "object" ? container : {};
  const itemsRaw = Array.isArray(raw.items) ? raw.items : [];
  const researchItemsById = new Map(
    (Array.isArray(researchItems) ? researchItems : []).map((ri) => [ri.id, ri])
  );

  const items = itemsRaw.
  map((it, idx) =>
  normalizeInsightItem(it, idx, researchItemsById.size, researchItemsById)
  ).
  filter(Boolean);
  const reviewStatusRaw = typeof raw.reviewStatus === "string" ? raw.reviewStatus.trim().toLowerCase() : "";
  const isReviewed = raw.isReviewed === true || reviewStatusRaw === "reviewed";
  const reviewStatus = isReviewed ? "reviewed" : "draft";
  const reviewedAt = Number.isFinite(Number(raw.reviewedAt)) ? Number(raw.reviewedAt) : null;
  const emergingSignals =
  Array.isArray(raw.emergingSignals) ?
  { items: raw.emergingSignals } :
  raw.emergingSignals && typeof raw.emergingSignals === "object" ?
  raw.emergingSignals :
  { items: [] };

  return {
    version: Number.isFinite(Number(raw.version)) ? Number(raw.version) : 1,
    items,
    createdAt: Number.isFinite(Number(raw.createdAt)) ?
    Number(raw.createdAt) :
    t,
    updatedAt: Number.isFinite(Number(raw.updatedAt)) ?
    Number(raw.updatedAt) :
    t,
    reviewStatus,
    reviewedAt,
    emergingSignals
  };
}

function resetInsightsReviewStatusIfEdited(container, didEdit) {
  if (!didEdit) return container;
  if (!container || container.reviewStatus !== "reviewed") return container;
  // If any insight changes after review, the review status must reset to avoid stale approvals.
  return { ...container, reviewStatus: "draft", reviewedAt: null };
}

function markInsightsApprovalStaleIfNeeded(initiative, didEdit) {
  if (!initiative) return initiative;
  if (!didEdit) return initiative;
  const next = { ...initiative, insightsEditedAt: now() };
  if (initiative.insightsApprovalStatus !== "approved") return next;
  return { ...next, insightsApprovalStatus: "stale" };
}

function touchInsightsDraft(initiative, actor, ts) {
  if (!initiative) return initiative;
  const t = Number.isFinite(Number(ts)) ? Number(ts) : now();
  const next = { ...initiative, insightsEditedAt: t, insightsEditedBy: actor };
  if (next.insightsApprovalStatus === "approved") {
    next.insightsApprovalStatus = "stale";
  }
  return next;
}

function syncActiveInsightsSet(initiative, ts) {
  if (!initiative) return initiative;
  const t = Number.isFinite(Number(ts)) ? Number(ts) : now();
  const activeSet = initiative.activeInsightsSet === "manual" ? "manual" : "auto";
  const currentSets =
  initiative.insightsSets && typeof initiative.insightsSets === "object" ?
  initiative.insightsSets :
  {};
  const activeContainer =
  currentSets[activeSet] && typeof currentSets[activeSet] === "object" ?
  currentSets[activeSet] :
  {};

  return {
    ...initiative,
    insightsSets: {
      ...currentSets,
      [activeSet]: {
        ...activeContainer,
        researchInsights: initiative.researchInsights,
        insightGroups: initiative.insightGroups,
        updatedAt: t
      }
    }
  };
}

function isInsightApproved(insight) {
  const status = typeof insight?.reviewStatus === "string" ? insight.reviewStatus : "";
  if (!status) return true;
  return status === "approved" || status === "reviewed";
}

const DECISION_STATUS_SET = new Set(["draft", "committed", "revisit"]);
const DELIVERY_PRIORITY_SET = new Set(["high", "medium", "low"]);

function normalizeDecisionDeliveryStories(rawStories, decisionId, fallbackRationale = "") {
  const items = Array.isArray(rawStories) ? rawStories : [];
  const seen = new Set();

  return items.
  map((story, idx) => {
    if (!story || typeof story !== "object") return null;
    const title = safeText(story.title).trim();
    const storyText = safeText(story.story).trim();
    if (!title || !storyText) return null;

    const idRaw = safeText(story.id).trim();
    const id = idRaw || `delivery_${safeText(decisionId || "decision").trim() || "decision"}_${idx + 1}`;
    if (seen.has(id)) return null;
    seen.add(id);

    const priorityRaw = safeText(story.priority).trim().toLowerCase();
    const priority = DELIVERY_PRIORITY_SET.has(priorityRaw) ? priorityRaw : "medium";

    const linkedInsightIds = normalizeStringArray(story.linkedInsightIds);
    const acceptanceCriteria = Array.isArray(story.acceptanceCriteria) ?
    story.acceptanceCriteria.map((line) => safeText(line).trim()).filter(Boolean) :
    typeof story.acceptanceCriteria === "string" ?
    story.acceptanceCriteria.
    split(/\r?\n/).
    map((line) => safeText(line).trim()).
    filter(Boolean) :
    [];
    const rationale =
    safeText(story.rationale).trim() ||
    safeText(fallbackRationale).trim() ||
    "Grounded in the confirmed decision and linked evidence.";

    return {
      id,
      decisionId: safeText(story.decisionId).trim() || safeText(decisionId).trim() || null,
      title,
      story: storyText,
      rationale,
      priority,
      acceptanceCriteria,
      linkedInsightIds,
      origin: safeText(story.origin).trim() || "ai",
      isEdited: story.isEdited === true,
      isReviewed: story.isReviewed === true,
      isLocked: story.isLocked === true
    };
  }).
  filter(Boolean);
}

function normalizeDecisionItem(rawDecision, idx, insightsById) {
  const raw = rawDecision && typeof rawDecision === "object" ? rawDecision : {};
  const title = typeof raw.title === "string" ? raw.title.trim() : "";
  if (!title) return null;

  const description = typeof raw.description === "string" ? raw.description.trim() : "";
  const projectId =
  typeof raw.projectId === "string" && raw.projectId.trim() ? raw.projectId.trim() : null;
  const initiativeId =
  typeof raw.initiativeId === "string" && raw.initiativeId.trim() ?
  raw.initiativeId.trim() :
  null;
  const linkedRaw = Array.isArray(raw.linkedInsightIds) ?
  raw.linkedInsightIds :
  Array.isArray(raw.insightIds) ?
  raw.insightIds :
  Array.isArray(raw.relatedInsightIds) ?
  raw.relatedInsightIds :
  [];
  const linkedInsightIds = linkedRaw.
  map((id) => typeof id === "string" ? id.trim() : "").
  filter(Boolean);
  const legacyEvidence = normalizeDecisionEvidenceItems(raw.evidence);
  const evidenceResearchItemIds = legacyEvidence.map((ev) => ev.researchItemId).filter(Boolean);
  const linkedResearchItemIds = Array.from(
    new Set([
    ...normalizeStringArray(raw.linkedResearchItemIds),
    ...normalizeStringArray(raw.researchItemIds),
    ...evidenceResearchItemIds]
    )
  );

  const filteredIds = Array.from(new Set(linkedInsightIds));

  const statusRaw = typeof raw.status === "string" ? raw.status.trim() : "draft";
  let status = DECISION_STATUS_SET.has(statusRaw) ? statusRaw : "draft";
  const hasMissingLinkedInsights = insightsById && filteredIds.some((id) => !insightsById.has(id));
  if (status !== "committed") {
    if (status === "revisit" && !hasMissingLinkedInsights) status = "draft";
    if (hasMissingLinkedInsights) status = "revisit";
  }

  const createdBy =
  typeof raw.createdBy === "string" && raw.createdBy.trim() ? raw.createdBy.trim() : "local-user";
  const updatedBy =
  typeof raw.updatedBy === "string" && raw.updatedBy.trim() ? raw.updatedBy.trim() : createdBy;
  const createdAt = typeof raw.createdAt === "string" && raw.createdAt.trim() ? raw.createdAt.trim() : isoNow();
  const updatedAt = typeof raw.updatedAt === "string" && raw.updatedAt.trim() ? raw.updatedAt.trim() : null;
  const committedAt =
  typeof raw.committedAt === "string" && raw.committedAt.trim() ? raw.committedAt.trim() : null;
  const committedBy =
  typeof raw.committedBy === "string" && raw.committedBy.trim() ? raw.committedBy.trim() : null;
  const committedInsightsVersion = Number.isFinite(Number(raw.committedInsightsVersion)) ?
  Number(raw.committedInsightsVersion) :
  null;
  const committedInsightIds = normalizeStringArray(raw.committedInsightIds);
  const staleFromInsights = raw.staleFromInsights === true;
  const staleReason = safeText(raw.staleReason).trim() || null;
  const staleAt = typeof raw.staleAt === "string" && raw.staleAt.trim() ? raw.staleAt.trim() : null;
  const confirmationSnapshot = normalizeDecisionConfirmationSnapshot(raw.confirmationSnapshot);
  const deliveryStories = normalizeDecisionDeliveryStories(raw.deliveryStories, raw.id || `dec_${idx + 1}`, description);
  if (raw.evidence !== undefined || legacyEvidence.length > 0) {
    logDecisionEvidenceDebug("normalize", {
      decisionId: safeText(raw.id).trim() || `dec_${idx + 1}`,
      title,
      rawEvidenceCount: Array.isArray(raw.evidence) ? raw.evidence.length : 0,
      normalizedEvidenceCount: legacyEvidence.length,
      linkedInsightIdsRaw: linkedInsightIds,
      linkedInsightIdsRendered: filteredIds,
      linkedResearchItemIdsRendered: linkedResearchItemIds
    });
  }

  return {
    id: typeof raw.id === "string" && raw.id.trim() ? raw.id.trim() : `dec_${idx + 1}`,
    title,
    description,
    ...(projectId ? { projectId } : {}),
    initiativeId,
    linkedInsightIds: filteredIds,
    linkedResearchItemIds,
    evidence: legacyEvidence,
    status,
    committedAt,
    committedBy,
    committedInsightsVersion,
    committedInsightIds,
    ...(staleFromInsights ? { staleFromInsights: true } : {}),
    ...(staleReason ? { staleReason } : {}),
    ...(staleAt ? { staleAt } : {}),
    ...(confirmationSnapshot ? { confirmationSnapshot } : {}),
    deliveryStories,
    createdBy,
    updatedBy,
    createdAt,
    ...(updatedAt ? { updatedAt } : {})
  };
}

function normalizeDecisions(decisions, insights) {
  const items = Array.isArray(decisions) ? decisions : [];
  const insightsById = new Map(
    (Array.isArray(insights) ? insights : []).map((insight) => [insight.id, insight])
  );

  return items.map((it, idx) => normalizeDecisionItem(it, idx, insightsById)).filter(Boolean);
}

function getDecisionInsights(initiative) {
  const approved = Array.isArray(initiative?.insightsApprovedSnapshot?.approvedInsights) ?
  initiative.insightsApprovedSnapshot.approvedInsights :
  [];
  if (approved.length) return approved;
  return Array.isArray(initiative?.researchInsights?.items) ? initiative.researchInsights.items : [];
}

function reconcileDecisionsWithInsights(decisions, insights, initiativeId) {
  const tIso = isoNow();
  const insightsById = new Map(
    (Array.isArray(insights) ? insights : []).map((insight) => [insight.id, insight])
  );
  const parseDecisionTimestamp = (value) => {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    const raw = safeText(value).trim();
    if (!raw) return 0;
    const asNumber = Number(raw);
    if (Number.isFinite(asNumber)) return asNumber;
    const asDate = Date.parse(raw);
    return Number.isFinite(asDate) ? asDate : 0;
  };
  const isCommittedDecisionStale = (decision) => {
    const committedAtTs = parseDecisionTimestamp(decision?.committedAt);
    const committedInsightIds = normalizeStringArray(decision?.committedInsightIds);
    if (!committedAtTs || !committedInsightIds.length) return false;
    return committedInsightIds.some((insightId) => {
      const insight = insightsById.get(insightId);
      if (!insight) return true;
      const insightUpdatedAt = parseDecisionTimestamp(insight?.updatedAt);
      return insightUpdatedAt > committedAtTs;
    });
  };

  const activityEntries = [];
  const nextDecisions = (Array.isArray(decisions) ? decisions : []).map((decision) => {
    const linkedRaw = Array.isArray(decision?.linkedInsightIds) ? decision.linkedInsightIds : [];
    const validIds = linkedRaw.filter((id) => {
      const insight = insightsById.get(id);
      if (!insight) return true;
      return isInsightApproved(insight);
    });

    const linksChanged = validIds.length !== linkedRaw.length;
    const isCommitted = String(decision?.status || "") === "committed";
    const nextStatus = isCommitted ? decision.status : linksChanged ? "revisit" : decision.status;
    const statusChanged = nextStatus !== decision.status;

    if (isCommitted) {
      const shouldMarkStale = isCommittedDecisionStale(decision);
      if (shouldMarkStale) {
        if (decision?.staleFromInsights === true) return decision;
        const nextDecision = {
          ...decision,
          staleFromInsights: true,
          staleReason: "linked_insight_changed_after_confirmation",
          staleAt: tIso
        };
        activityEntries.push(
          makeActivityEntry({
            entityType: "decision",
            entityId: nextDecision.id,
            initiativeId: initiativeId ?? null,
            action: "edited",
            actor: "system",
            timestamp: tIso,
            shortDescription: "Confirmed decision marked stale after linked insights changed."
          })
        );
        return nextDecision;
      }
      if (decision?.staleFromInsights !== true) return decision;
      const nextDecision = {
        ...decision,
        staleFromInsights: false,
        staleReason: null,
        staleAt: null
      };
      return nextDecision;
    }

    if (!linksChanged && !statusChanged) return decision;

    const nextDecision = {
      ...decision,
      linkedInsightIds: validIds,
      status: nextStatus,
      updatedBy: "system",
      updatedAt: tIso
    };

    activityEntries.push(
      makeActivityEntry({
        entityType: "decision",
        entityId: nextDecision.id,
        initiativeId: initiativeId ?? null,
        action: "edited",
        actor: "system",
        timestamp: tIso,
        shortDescription: "Linked insights changed; decision set to revisit."
      })
    );

    return nextDecision;
  });

  return { decisions: nextDecisions, activityEntries };
}

const FEEDBACK_TYPES = new Set([
"bug",
"idea",
"ai_quality",
"ux_confusion",
"data_issue",
"other"]
);

const FEEDBACK_STATUSES = new Set(["new", "acknowledged", "done"]);

const FEEDBACK_SEVERITIES = new Set(["low", "medium", "high"]);

function normalizeFeedbackContext(rawContext, { route, activeProjectId, activeInitiativeId } = {}) {
  const ctx = rawContext && typeof rawContext === "object" ? rawContext : {};
  const mergedRoute = typeof ctx.route === "string" && ctx.route.trim() ? ctx.route.trim() : route || "/";
  const mergedProjectId =
  typeof ctx.activeProjectId === "string" && ctx.activeProjectId.trim() ?
  ctx.activeProjectId.trim() :
  activeProjectId || null;
  const mergedInitiativeId =
  typeof ctx.activeInitiativeId === "string" && ctx.activeInitiativeId.trim() ?
  ctx.activeInitiativeId.trim() :
  activeInitiativeId || null;

  return {
    route: mergedRoute,
    activeProjectId: mergedProjectId,
    activeInitiativeId: mergedInitiativeId,
    entityType:
    typeof ctx.entityType === "string" && ctx.entityType.trim() ? ctx.entityType.trim() : null,
    entityId: typeof ctx.entityId === "string" && ctx.entityId.trim() ? ctx.entityId.trim() : null,
    ...(ctx.schemaVersion != null ? { schemaVersion: ctx.schemaVersion } : {}),
    ...(typeof ctx.appVersion === "string" && ctx.appVersion.trim() ? { appVersion: ctx.appVersion.trim() } : {}),
    ...(typeof ctx.isAI === "boolean" ? { isAI: ctx.isAI } : {})
  };
}

function normalizeFeedbackItem(rawEntry, idx, fallbackContext, currentUserId) {
  const entry = rawEntry && typeof rawEntry === "object" ? rawEntry : {};
  const typeRaw = typeof entry.type === "string" ? entry.type : "other";
  const type = FEEDBACK_TYPES.has(typeRaw) ? typeRaw : "other";
  const statusRaw = typeof entry.status === "string" ? entry.status : "new";
  const status = FEEDBACK_STATUSES.has(statusRaw) ? statusRaw : "new";
  const severityRaw = typeof entry.severity === "string" ? entry.severity : null;
  const severity = FEEDBACK_SEVERITIES.has(severityRaw) ? severityRaw : null;
  const message = typeof entry.message === "string" ? entry.message.trim() : "";
  if (!message) return null;

  const createdAt =
  typeof entry.createdAt === "string" && entry.createdAt.trim() ? entry.createdAt.trim() : isoNow();
  const createdByUserId =
  typeof entry.createdByUserId === "string" && entry.createdByUserId.trim() ?
  entry.createdByUserId.trim() :
  currentUserId || "local-user";

  return {
    id: typeof entry.id === "string" && entry.id.trim() ? entry.id.trim() : `fb_${idx + 1}`,
    type,
    severity,
    status,
    message,
    createdAt,
    createdByUserId,
    context: normalizeFeedbackContext(entry.context, fallbackContext),
    ...(entry.meta && typeof entry.meta === "object" ? { meta: entry.meta } : {})
  };
}

function normalizeFeedbackList(list, fallbackContext, currentUserId) {
  const items = Array.isArray(list) ? list : [];
  return items.map((it, idx) => normalizeFeedbackItem(it, idx, fallbackContext, currentUserId)).filter(Boolean);
}

function makeActivityEntry(payload) {
  const entry = payload && typeof payload === "object" ? payload : {};
  const entityType = typeof entry.entityType === "string" ? entry.entityType : "overview";
  const action = typeof entry.action === "string" ? entry.action : "edited";
  const actor = typeof entry.actor === "string" ? entry.actor : "user";
  const entityId = safeText(entry.entityId).trim();
  if (!entityId) return null;

  const shortDescription = safeText(entry.shortDescription).trim();
  return {
    id: safeText(entry.id).trim() || uid("act"),
    entityType,
    entityId,
    initiativeId: entry.initiativeId ?? null,
    action,
    actor,
    timestamp: typeof entry.timestamp === "string" && entry.timestamp.trim() ? entry.timestamp.trim() : isoNow(),
    shortDescription
  };
}

function appendActivity(project, entries) {
  const list = Array.isArray(entries) ? entries : entries ? [entries] : [];
  if (!list.length) return project;
  const next = { ...project };
  const current = Array.isArray(next.activity) ? next.activity : [];
  next.activity = [...current, ...list];
  return next;
}

function normalizeInsightGroups(container) {
  const t = now();
  const raw = container && typeof container === "object" ? container : null;
  const version = Number.isFinite(Number(raw?.version)) ? Number(raw.version) : 1;
  const groupsRaw = Array.isArray(raw?.groups) ? raw.groups : [];
  const allowedStatuses = new Set(["draft", "needs_review", "approved"]);

  const groups = groupsRaw.
  map((g, idx) => {
    if (!g || typeof g !== "object") return null;
    const title =
    typeof g.title === "string" && g.title.trim() ? g.title.trim() : "Ungrouped insights";
    const rawId = typeof g.id === "string" && g.id.trim() ? g.id.trim() : "";
    const id = rawId || `grp_${title.toLowerCase().replace(/[^a-z0-9]+/g, "_")}`;
    const order = Number.isFinite(Number(g.order)) ? Number(g.order) : idx;
    const summary = typeof g.summary === "string" ? g.summary.trim() : "";
    const notes = typeof g.notes === "string" ? g.notes : "";
    const statusRaw = typeof g.status === "string" ? g.status.trim().toLowerCase() : "";
    const status = allowedStatuses.has(statusRaw) ? statusRaw : "draft";
    const createdAt = Number.isFinite(Number(g.createdAt)) ?
    Number(g.createdAt) :
    t;
    const updatedAt = Number.isFinite(Number(g.updatedAt)) ?
    Number(g.updatedAt) :
    createdAt;
    return { id, title, summary, notes, status, order, createdAt, updatedAt };
  }).
  filter(Boolean).
  sort((a, b) => a.order - b.order);

  if (!groups.length) {
    groups.push({ id: "grp_ungrouped", title: "Ungrouped insights", order: 0, createdAt: t, updatedAt: t });
  }

  return {
    version,
    groups,
    createdAt: Number.isFinite(Number(raw?.createdAt)) ?
    Number(raw.createdAt) :
    t,
    updatedAt: Number.isFinite(Number(raw?.updatedAt)) ?
    Number(raw.updatedAt) :
    t
  };
}

function syncInsightGroups(groupsContainer, items, tagThemes) {
  const t = now();
  const cont = normalizeInsightGroups(groupsContainer);
  const groupById = new Map(cont.groups.map((g) => [g.id, g]));
  const titleToGroupId = new Map(
    cont.groups.map((g) => [
    String(g.title || "").
    toLowerCase().
    replace(/[^a-z0-9]+/g, " ").
    trim(),
    g.id]
    )
  );

  const themeItems = Array.isArray(tagThemes?.themes) ? tagThemes.themes : [];
  const themeTagToGroupId = new Map();
  themeItems.forEach((th) => {
    const themeId =
    typeof th?.id === "string" && th.id.trim() ?
    th.id.trim() :
    `grp_${String(th?.title || "theme").toLowerCase().replace(/[^a-z0-9]+/g, "_")}`;
    const themeTitle =
    typeof th?.title === "string" && th.title.trim() ?
    th.title.trim() :
    themeId.replace(/^grp_/, "").replace(/_/g, " ");
    if (!groupById.has(themeId)) {
      groupById.set(themeId, {
        id: themeId,
        title: themeTitle,
        order: groupById.size,
        createdAt: t,
        updatedAt: t
      });
    }
    (Array.isArray(th?.tags) ? th.tags : []).forEach((tag) => {
      const key = safeLowerTag(tag);
      if (key && !themeTagToGroupId.has(key)) themeTagToGroupId.set(key, themeId);
    });
  });

  const nextItems = (Array.isArray(items) ? items : []).map((it) => {
    const baseGroupId =
    typeof it.groupId === "string" && it.groupId.trim() ?
    it.groupId.trim() :
    "grp_ungrouped";

    let gid = baseGroupId;
    // Prefer theme grouping only when insight is currently ungrouped.
    // Keep AI-assigned/non-ungrouped groupIds as source of truth.
    if (gid === "grp_ungrouped" && Array.isArray(it.tags) && it.tags.length) {
      for (const rawTag of it.tags) {
        const tagKey = safeLowerTag(rawTag);
        if (!tagKey) continue;
        const mappedTheme = themeTagToGroupId.get(tagKey);
        if (mappedTheme) {
          gid = mappedTheme;
          break;
        }
      }
    }
    if (gid === "grp_ungrouped" && Array.isArray(it.tags) && it.tags.length) {
      const tagKey = String(it.tags[0] || "").
      toLowerCase().
      replace(/[^a-z0-9]+/g, " ").
      trim();
      const mapped = titleToGroupId.get(tagKey);
      if (mapped) gid = mapped;
    }

    if (!groupById.has(gid)) {
      groupById.set(gid, {
        id: gid,
        title:
        gid === "grp_ungrouped" ?
        "Ungrouped insights" :
        gid.replace(/^grp_/, "").replace(/_/g, " "),
        order: groupById.size,
        createdAt: t,
        updatedAt: t
      });
    }

    return { ...it, groupId: gid };
  });

  const usedGroupIds = new Set(nextItems.map((it) => it.groupId || "grp_ungrouped"));
  // Preserve existing groups so newly created empty themes stay visible.
  cont.groups.forEach((g) => {
    if (g?.id) usedGroupIds.add(g.id);
  });
  usedGroupIds.add("grp_ungrouped");

  const groups = Array.from(groupById.values()).
  filter((g) => usedGroupIds.has(g.id)).
  map((g, idx) => ({
    ...g,
    order: Number.isFinite(Number(g.order)) ? Number(g.order) : idx
  }));

  return { groups: { ...cont, groups, updatedAt: t }, items: nextItems };
}

/* ------------------------------------------------------------------ */
/* Snapshot cache                                                     */
/* ------------------------------------------------------------------ */

let _activeSnapshotCache = null;
let _activeSnapshotKey = "";

function buildStableActiveSnapshot(state) {
  const wsId = state.activeWorkspaceId || "";
  const pid = wsId && state.activeProjectByWorkspaceId?.[wsId] || "";
  const rawProj = pid ? state.projects?.byId?.[pid] || null : null;

  const projStamp = rawProj?.meta?.updatedAt ?? rawProj?.updatedAt ?? "";
  const proj = rawProj ? normalizeProject(rawProj) : null;

  const initId =
  pid && state.activeInitiativeByProjectId?.[pid] ||
  proj?.initiatives?.activeId ||
  proj?.initiatives?.allIds?.[0] ||
  "";

  const rawInit =
  initId && rawProj?.initiatives?.byId ?
  rawProj.initiatives.byId[initId] || null :
  null;

  const initStamp = rawInit?.updatedAt ?? "";
  const key = `${wsId}::${pid}::${initId}::${projStamp}::${initStamp}`;

  if (_activeSnapshotCache && _activeSnapshotKey === key) return _activeSnapshotCache;

  const init = initId ? proj?.initiatives?.byId?.[initId] || null : null;

  _activeSnapshotKey = key;
  _activeSnapshotCache = {
    activeWorkspaceId: wsId || null,
    activeProjectId: pid || null,
    activeInitiativeId: initId || null,
    project: proj,
    initiative: init
  };

  return _activeSnapshotCache;
}

function invalidateActiveSnapshotCache() {
  _activeSnapshotKey = "";
  _activeSnapshotCache = null;
}

function ensureActiveInitiativeForProject(proj, fallbackId = null) {
  const p = normalizeProject(proj);
  const initId = fallbackId || p?.initiatives?.activeId || p?.initiatives?.allIds?.[0] || null;
  return { project: p, initId };
}
/* ------------------------------------------------------------------ */
/* Store                                                              */
/* ------------------------------------------------------------------ */

export const useProjectStore = create(
  (set, get) => ({
    schemaVersion: 3,
    hasHydrated: true,
    workspaces: { byId: {}, allIds: [] },
    activeWorkspaceId: null,
    projects: { byId: {}, allIds: [] },
    activeProjectByWorkspaceId: {},
    activeInitiativeByProjectId: {},
    researchFocus: null,
    targetInsightId: null,
    isGeneratingProject: false,
    generationError: null,
    generationPayload: null,
    manualMapping: {
      startDraftFromAutoSuggestion() {
        const { activeProjectId } = get().selectActiveRefs();
        if (!activeProjectId) return;

        invalidateActiveSnapshotCache();

        set((s) => {
          const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
          if (!proj0) return s;

          const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
          const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
          if (!initId) return s;

          const currentInit = proj.initiatives.byId[initId];
          const facts = safeArray(currentInit.atomicFacts?.items);
          const factsById = new Map(facts.map((f) => [f.id, f]));
          const insights = safeArray(currentInit.researchInsights?.items);
          const groupContainer = normalizeInsightGroups(currentInit.insightGroups);
          const groupSeeds = groupContainer.groups.map((g, idx) => ({
            id: `mm_${g.id}`,
            title: safeText(g.title).trim() || `Group ${idx + 1}`,
            sourceGroupId: g.id,
            atomicFactIds: [],
            order: Number.isFinite(Number(g.order)) ? Number(g.order) : idx
          }));
          const groupBySource = new Map(groupSeeds.map((g) => [g.sourceGroupId, g]));

          const assigned = new Set();
          insights.forEach((ins) => {
            const groupId = safeText(ins?.groupId).trim() || "grp_ungrouped";
            const factIds = safeArray(ins?.factIds);
            factIds.forEach((fid) => {
              const factId = safeText(fid).trim();
              if (!factId || assigned.has(factId) || !factsById.has(factId)) return;
              const target = groupBySource.get(groupId);
              if (!target) return;
              target.atomicFactIds.push(factId);
              assigned.add(factId);
            });
          });

          let draftGroups = [];
          let unassignedAtomicFactIds = [];

          if (assigned.size) {
            draftGroups = groupSeeds.
            filter((g) => g.atomicFactIds.length).
            sort((a, b) => a.order - b.order).
            map(({ id, title, atomicFactIds }) => ({ id, title, atomicFactIds }));
            unassignedAtomicFactIds = facts.
            map((f) => f.id).
            filter((id) => id && !assigned.has(id));
          } else {
            const tagGroups = new Map();
            facts.forEach((fact) => {
              const tags = safeArray(fact?.tags).filter(Boolean);
              const rawTag = tags[0] || "Other";
              const title = safeText(rawTag).trim() || "Other";
              const tagKey = safeLowerTag(title) || "other";
              if (!tagGroups.has(tagKey)) {
                tagGroups.set(tagKey, {
                  id: `mm_tag_${tagKey.replace(/[^a-z0-9]+/g, "_")}`,
                  title,
                  atomicFactIds: []
                });
              }
              tagGroups.get(tagKey).atomicFactIds.push(fact.id);
            });
            draftGroups = Array.from(tagGroups.values());
            unassignedAtomicFactIds = [];
          }

          const tIso = isoNow();
          const nextManualMapping = {
            isActive: true,
            draftGroups,
            unassignedAtomicFactIds,
            startedAt: tIso,
            lastEditedAt: tIso
          };

          const nextInit = { ...currentInit, manualMapping: nextManualMapping, updatedAt: now() };

          const nextProj = normalizeProject({
            ...proj,
            initiatives: {
              ...proj.initiatives,
              byId: { ...proj.initiatives.byId, [initId]: nextInit },
              activeId: initId
            },
            meta: { ...(proj.meta || {}), updatedAt: now() }
          });

          return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
        });
      },

      cancelDraft() {
        const { activeProjectId } = get().selectActiveRefs();
        if (!activeProjectId) return;

        invalidateActiveSnapshotCache();

        set((s) => {
          const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
          if (!proj0) return s;

          const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
          const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
          if (!initId) return s;

          const currentInit = proj.initiatives.byId[initId];
          const nextInit = {
            ...currentInit,
            manualMapping: { isActive: false, draftGroups: [], unassignedAtomicFactIds: [], startedAt: null, lastEditedAt: null },
            updatedAt: now()
          };

          const nextProj = normalizeProject({
            ...proj,
            initiatives: {
              ...proj.initiatives,
              byId: { ...proj.initiatives.byId, [initId]: nextInit },
              activeId: initId
            },
            meta: { ...(proj.meta || {}), updatedAt: now() }
          });

          return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
        });
      },

      saveDraft() {
        const { activeProjectId } = get().selectActiveRefs();
        if (!activeProjectId) return;

        invalidateActiveSnapshotCache();

        set((s) => {
          const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
          if (!proj0) return s;

          const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
          const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
          if (!initId) return s;

          const currentInit = proj.initiatives.byId[initId];
          const currentManual = normalizeManualMappingState(currentInit.manualMapping);
          const nextInit = {
            ...currentInit,
            manualMapping: { ...currentManual, lastEditedAt: isoNow() },
            updatedAt: now()
          };

          const nextProj = normalizeProject({
            ...proj,
            initiatives: {
              ...proj.initiatives,
              byId: { ...proj.initiatives.byId, [initId]: nextInit },
              activeId: initId
            },
            meta: { ...(proj.meta || {}), updatedAt: now() }
          });

          return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
        });
      },

      moveAtomicFact(factId, fromGroupId = null, toGroupId = null, index = null) {
        const id = safeText(factId).trim();
        if (!id) return;
        const { activeProjectId } = get().selectActiveRefs();
        if (!activeProjectId) return;

        invalidateActiveSnapshotCache();

        set((s) => {
          const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
          if (!proj0) return s;

          const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
          const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
          if (!initId) return s;

          const currentInit = proj.initiatives.byId[initId];
          const currentManual = normalizeManualMappingState(currentInit.manualMapping);
          let draftGroups = currentManual.draftGroups.map((g) => ({
            ...g,
            atomicFactIds: g.atomicFactIds.filter((fid) => fid !== id)
          }));
          let unassigned = currentManual.unassignedAtomicFactIds.filter((fid) => fid !== id);

          if (toGroupId) {
            draftGroups = draftGroups.map((g) => {
              if (g.id !== toGroupId) return g;
              const nextIds = g.atomicFactIds.slice();
              if (Number.isFinite(Number(index)) && index >= 0 && index <= nextIds.length) {
                nextIds.splice(index, 0, id);
              } else {
                nextIds.push(id);
              }
              return { ...g, atomicFactIds: nextIds };
            });
          } else {
            if (Number.isFinite(Number(index)) && index >= 0 && index <= unassigned.length) {
              unassigned.splice(index, 0, id);
            } else {
              unassigned.push(id);
            }
          }

          const nextInit = {
            ...currentInit,
            manualMapping: {
              ...currentManual,
              draftGroups,
              unassignedAtomicFactIds: unassigned,
              lastEditedAt: isoNow()
            },
            updatedAt: now()
          };

          const nextProj = normalizeProject({
            ...proj,
            initiatives: {
              ...proj.initiatives,
              byId: { ...proj.initiatives.byId, [initId]: nextInit },
              activeId: initId
            },
            meta: { ...(proj.meta || {}), updatedAt: now() }
          });

          return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
        });
      },

      createGroup(title = "New group") {
        const { activeProjectId } = get().selectActiveRefs();
        if (!activeProjectId) return;

        invalidateActiveSnapshotCache();

        set((s) => {
          const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
          if (!proj0) return s;

          const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
          const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
          if (!initId) return s;

          const currentInit = proj.initiatives.byId[initId];
          const currentManual = normalizeManualMappingState(currentInit.manualMapping);
          const nextGroups = [
          ...currentManual.draftGroups,
          { id: uid("mm_grp"), title: safeText(title).trim() || "New group", atomicFactIds: [] }];


          const nextInit = {
            ...currentInit,
            manualMapping: { ...currentManual, draftGroups: nextGroups, lastEditedAt: isoNow() },
            updatedAt: now()
          };

          const nextProj = normalizeProject({
            ...proj,
            initiatives: {
              ...proj.initiatives,
              byId: { ...proj.initiatives.byId, [initId]: nextInit },
              activeId: initId
            },
            meta: { ...(proj.meta || {}), updatedAt: now() }
          });

          return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
        });
      },

      renameGroup(groupId, title) {
        const gid = safeText(groupId).trim();
        if (!gid) return;
        const { activeProjectId } = get().selectActiveRefs();
        if (!activeProjectId) return;

        invalidateActiveSnapshotCache();

        set((s) => {
          const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
          if (!proj0) return s;

          const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
          const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
          if (!initId) return s;

          const currentInit = proj.initiatives.byId[initId];
          const currentManual = normalizeManualMappingState(currentInit.manualMapping);
          const nextGroups = currentManual.draftGroups.map((g) =>
          g.id === gid ? { ...g, title: safeText(title).trim() || g.title } : g
          );

          const nextInit = {
            ...currentInit,
            manualMapping: { ...currentManual, draftGroups: nextGroups, lastEditedAt: isoNow() },
            updatedAt: now()
          };

          const nextProj = normalizeProject({
            ...proj,
            initiatives: {
              ...proj.initiatives,
              byId: { ...proj.initiatives.byId, [initId]: nextInit },
              activeId: initId
            },
            meta: { ...(proj.meta || {}), updatedAt: now() }
          });

          return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
        });
      },

      deleteGroup(groupId) {
        const gid = safeText(groupId).trim();
        if (!gid) return;
        const { activeProjectId } = get().selectActiveRefs();
        if (!activeProjectId) return;

        invalidateActiveSnapshotCache();

        set((s) => {
          const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
          if (!proj0) return s;

          const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
          const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
          if (!initId) return s;

          const currentInit = proj.initiatives.byId[initId];
          const currentManual = normalizeManualMappingState(currentInit.manualMapping);
          const removed = currentManual.draftGroups.find((g) => g.id === gid);
          const nextGroups = currentManual.draftGroups.filter((g) => g.id !== gid);
          const nextUnassigned = Array.from(
            new Set([...(currentManual.unassignedAtomicFactIds || []), ...(removed?.atomicFactIds || [])])
          );

          const nextInit = {
            ...currentInit,
            manualMapping: {
              ...currentManual,
              draftGroups: nextGroups,
              unassignedAtomicFactIds: nextUnassigned,
              lastEditedAt: isoNow()
            },
            updatedAt: now()
          };

          const nextProj = normalizeProject({
            ...proj,
            initiatives: {
              ...proj.initiatives,
              byId: { ...proj.initiatives.byId, [initId]: nextInit },
              activeId: initId
            },
            meta: { ...(proj.meta || {}), updatedAt: now() }
          });

          return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
        });
      },

      async regenerateInsightsFromDraft() {
        const { activeProjectId } = get().selectActiveRefs();
        if (!activeProjectId) return { ok: false, reason: "no_project" };

        const proj0 = normalizeProject(get().projects.byId[activeProjectId]);
        if (!proj0) return { ok: false, reason: "no_project" };

        const mappedInitId = (get().activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return { ok: false, reason: "no_initiative" };

        const currentInit = proj.initiatives.byId[initId];
        const currentManual = normalizeManualMappingState(currentInit.manualMapping);
        const groupsWithFacts = currentManual.draftGroups.filter((g) => g.atomicFactIds.length > 0);

        if (!groupsWithFacts.length) {
          return { ok: false, reason: "empty_groups" };
        }

        const factsById = new Map(safeArray(currentInit.atomicFacts?.items).map((f) => [f.id, f]));
        const draftGroups = groupsWithFacts.map((g) => ({
          id: g.id,
          title: g.title,
          atomicFacts: g.atomicFactIds.map((id) => factsById.get(id)).filter(Boolean)
        }));

        const researchItems = safeArray(currentInit.researchItems);
        const researchItemIds = researchItems.map((item) => item.id).filter(Boolean);
        const promptFacts = Array.isArray(proj?.meta?.promptFacts) ? proj.meta.promptFacts : [];

        const formData = {
          projectName: safeText(proj?.meta?.projectName),
          productDescription: safeText(proj?.meta?.productDescription),
          targetAudience: safeText(proj?.meta?.targetAudience),
          industry: safeText(proj?.meta?.industry)
        };

        try {
          const { generateInsightsFromManualClusters } = await import("../utils/generateWithGroq.js");
          const parsed = await generateInsightsFromManualClusters({
            formData,
            promptFacts,
            researchItemIds,
            draftGroups
          });

          const items = safeArray(parsed?.overview?.researchInsights);
          if (!items.length) return { ok: false, reason: "no_insights" };

          set((s) => {
            const projNext = normalizeProject(s.projects.byId[activeProjectId]);
            if (!projNext) return s;
            const mappedInitIdNext = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
            const { project: projN, initId: initIdN } = ensureActiveInitiativeForProject(
              projNext,
              mappedInitIdNext
            );
            if (!initIdN) return s;
            const initNext = projN.initiatives.byId[initIdN];

            const nextInsights = normalizeInsightsContainer(
              { items, emergingSignals: parsed?.emergingSignals || { items: [], generatedAt: isoNow() } },
              initNext.researchItems
            );
            const nextGroups = normalizeInsightGroups(parsed?.insightGroups || { groups: [] });
            const synced = syncInsightGroups(nextGroups, nextInsights.items, initNext.tagThemes);
            const finalInsights = { ...nextInsights, items: synced.items };
            const finalGroups = synced.groups;

            const cleared = {
              isActive: false,
              draftGroups: [],
              unassignedAtomicFactIds: [],
              startedAt: null,
              lastEditedAt: null
            };

            const manualSet = {
              researchInsights: finalInsights,
              insightGroups: finalGroups,
              updatedAt: now()
            };
            const insightsSets = {
              ...(initNext.insightsSets && typeof initNext.insightsSets === "object" ? initNext.insightsSets : {}),
              manual: manualSet
            };

            let nextInit = {
              ...initNext,
              researchInsights: finalInsights,
              insightGroups: finalGroups,
              insightsSets,
              activeInsightsSet: "manual",
              insightsLastGeneratedAt: now(),
              insightsEditedAt: now(),
              insightsEditedBy: "AI",
              manualMapping: cleared,
              updatedAt: now()
            };
            const nextProblem = safeText(parsed?.problemDefinition || "").trim();
            const nextWeWill = safeText(parsed?.weWill || "").trim();
            if (nextProblem || nextWeWill) {
              const currentProblemDef =
              initNext?.problemDefinition && typeof initNext.problemDefinition === "object" ?
              initNext.problemDefinition :
              {};
              nextInit.problemDefinition = {
                ...currentProblemDef,
                problem: nextProblem || safeText(currentProblemDef.problem || "").trim(),
                weWill: nextWeWill || safeText(currentProblemDef.weWill || "").trim()
              };
            }
            nextInit = markInsightsApprovalStaleIfNeeded(nextInit, true);

            const nextProj = normalizeProject({
              ...projN,
              initiatives: {
                ...projN.initiatives,
                byId: { ...projN.initiatives.byId, [initIdN]: nextInit },
                activeId: initIdN
              },
              meta: { ...(projN.meta || {}), updatedAt: now() }
            });

            return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
          });

          return { ok: true };
        } catch (err) {
          void 0;
          return { ok: false, reason: "generation_failed" };
        }
      }
    },
    currentUser: null,
    feedback: [],
    isOverviewGenerating: false,

    setHasHydrated(next = true) {
      set({ hasHydrated: Boolean(next) });
    },

    setResearchFocus(focus) {
      const rid = safeText(focus?.researchItemId).trim();
      if (!rid) {
        set({ researchFocus: null });
        return;
      }
      const start = Number.isFinite(Number(focus?.start)) ? Number(focus.start) : null;
      const end = Number.isFinite(Number(focus?.end)) ? Number(focus.end) : null;
      const text = typeof focus?.text === "string" ? focus.text : "";
      set({
        researchFocus: {
          researchItemId: rid,
          ...(Number.isFinite(start) ? { start } : {}),
          ...(Number.isFinite(end) ? { end } : {}),
          ...(text ? { text } : {})
        }
      });
    },

    clearResearchFocus() {
      set({ researchFocus: null });
    },

    setTargetInsightId(insightId) {
      const nextId = safeText(insightId).trim();
      set({ targetInsightId: nextId || null });
    },

    clearTargetInsightId() {
      set({ targetInsightId: null });
    },

    setIsGeneratingProject(next) {
      set({ isGeneratingProject: Boolean(next) });
    },

    setGenerationError(message) {
      set({ generationError: message || null });
    },

    setGenerationPayload(payload) {
      set({ generationPayload: payload || null });
    },

    setIsOverviewGenerating(next) {
      set({ isOverviewGenerating: Boolean(next) });
    },

    setCurrentUser(user) {
      const nextUser = user && typeof user === "object" ? user : {};
      const id =
      typeof nextUser.id === "string" && nextUser.id.trim() ? nextUser.id.trim() : "";
      if (!id) {
        set({ currentUser: null });
        return;
      }
      const name =
      typeof nextUser.name === "string" && nextUser.name.trim() ? nextUser.name.trim() : "";
      const email =
      typeof nextUser.email === "string" && nextUser.email.trim() ? nextUser.email.trim() : "";
      const role = nextUser.role === "member" ? "member" : "admin";
      const plan = nextUser.plan === "demo" ? "demo" : "free";
      set({ currentUser: { id, name, email, role, plan } });
    },

    addNoteFromSelection({
      researchItemId,
      initiativeId,
      startOffset,
      endOffset,
      selectedText,
      message
    }) {
      const rid = safeText(researchItemId).trim();
      const msg = safeText(message).trim();
      const start = Number(startOffset);
      const end = Number(endOffset);
      if (!rid || !msg || !Number.isFinite(start) || !Number.isFinite(end) || end <= start) return;

      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId) return;

      const noteId = uid("note");
      set((s) => {
        let proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const ensured = ensureActiveInitiativeForProject(proj0, mappedInitId);
        proj0 = ensured.project;
        const initId =
        initiativeId && proj0?.initiatives?.byId?.[initiativeId] ?
        initiativeId :
        ensured.initId;
        if (!initId) return s;

        const currentInit = proj0.initiatives.byId[initId];
        const currentUser = s.currentUser || { id: "local-user" };
        const tIso = isoNow();

        const newNote = {
          id: noteId,
          researchItemId: rid,
          initiativeId: initId,
          startOffset: start,
          endOffset: end,
          selectedText: typeof selectedText === "string" ? selectedText : "",
          message: msg,
          status: "open",
          createdBy: currentUser.id,
          createdAt: tIso,
          updatedBy: currentUser.id,
          updatedAt: tIso,
          replies: []
        };

        const nextInit = {
          ...currentInit,
          notes: [...safeArray(currentInit.notes), newNote],
          updatedAt: now()
        };

        const activityEntry = makeActivityEntry({
          entityType: "note",
          entityId: newNote.id,
          initiativeId: initId,
          action: "created",
          actor: "user",
          timestamp: tIso,
          shortDescription: `Note added to ${rid}.`
        });

        const nextProj = normalizeProject({
          ...appendActivity(proj0, activityEntry),
          initiatives: {
            ...proj0.initiatives,
            byId: { ...proj0.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj0.meta || {}), updatedAt: now() }
        });

        return {
          projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } },
          activeInitiativeByProjectId: { ...(s.activeInitiativeByProjectId || {}), [activeProjectId]: initId }
        };
      });

      invalidateActiveSnapshotCache();

      return noteId;
    },

    replyToNote(noteId, message) {
      const id = safeText(noteId).trim();
      const msg = safeText(message).trim();
      if (!id || !msg) return;

      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId) return;

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const currentNotes = safeArray(currentInit.notes);
        const currentUser = s.currentUser || { id: "local-user" };
        const tIso = isoNow();

        let didUpdate = false;
        const nextNotes = currentNotes.map((note) => {
          if (note?.id !== id) return note;
          didUpdate = true;
          const replies = safeArray(note.replies);
          const nextReplies = [
          ...replies,
          { id: uid("reply"), message: msg, createdBy: currentUser.id, createdAt: tIso }];

          return {
            ...note,
            replies: nextReplies,
            updatedBy: currentUser.id,
            updatedAt: tIso
          };
        });

        if (!didUpdate) return s;

        const nextInit = { ...currentInit, notes: nextNotes, updatedAt: now() };
        const nextProj = normalizeProject({
          ...proj,
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: now() }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });

      invalidateActiveSnapshotCache();
    },

    resolveNote(noteId) {
      const id = safeText(noteId).trim();
      if (!id) return;

      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId) return;

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const currentNotes = safeArray(currentInit.notes);
        const currentUser = s.currentUser || { id: "local-user" };
        const tIso = isoNow();

        let didUpdate = false;
        const nextNotes = currentNotes.map((note) => {
          if (note?.id !== id) return note;
          didUpdate = true;
          return {
            ...note,
            status: "resolved",
            updatedBy: currentUser.id,
            updatedAt: tIso
          };
        });

        if (!didUpdate) return s;

        const activityEntry = makeActivityEntry({
          entityType: "note",
          entityId: id,
          initiativeId: initId,
          action: "edited",
          actor: "user",
          timestamp: tIso,
          shortDescription: "Note resolved."
        });

        const nextInit = { ...currentInit, notes: nextNotes, updatedAt: now() };
        const nextProj = normalizeProject({
          ...appendActivity(proj, activityEntry),
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: now() }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });

      invalidateActiveSnapshotCache();
    },

    reopenNote(noteId) {
      const id = safeText(noteId).trim();
      if (!id) return;

      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId) return;

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const currentNotes = safeArray(currentInit.notes);
        const currentUser = s.currentUser || { id: "local-user" };
        const tIso = isoNow();

        let didUpdate = false;
        const nextNotes = currentNotes.map((note) => {
          if (note?.id !== id) return note;
          didUpdate = true;
          return {
            ...note,
            status: "open",
            updatedBy: currentUser.id,
            updatedAt: tIso
          };
        });

        if (!didUpdate) return s;

        const nextInit = { ...currentInit, notes: nextNotes, updatedAt: now() };
        const nextProj = normalizeProject({
          ...proj,
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: now() }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });

      invalidateActiveSnapshotCache();
    },

    deleteNote(noteId) {
      const id = safeText(noteId).trim();
      if (!id) return;

      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId) return;

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const currentNotes = safeArray(currentInit.notes);
        const nextNotes = currentNotes.filter((note) => note?.id !== id);
        if (nextNotes.length === currentNotes.length) return s;

        const nextInit = { ...currentInit, notes: nextNotes, updatedAt: now() };
        const nextProj = normalizeProject({
          ...proj,
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: now() }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });

      invalidateActiveSnapshotCache();
    },

    submitFeedback({ type, severity, message, context, meta } = {}) {
      const currentUser = get().currentUser || { id: "local-user", role: "admin" };
      const activeRefs = get().selectActiveRefs();
      const route =
      typeof window !== "undefined" && window.location ? window.location.pathname : "/";
      const fallbackContext = {
        route,
        activeProjectId: activeRefs?.activeProjectId || null,
        activeInitiativeId: activeRefs?.activeInitiativeId || null
      };

      const entry = normalizeFeedbackItem(
        {
          id: uid("fb"),
          type,
          severity,
          message,
          status: "new",
          createdByUserId: currentUser.id,
          createdAt: isoNow(),
          context: normalizeFeedbackContext(context, fallbackContext),
          ...(meta && typeof meta === "object" ? { meta } : {})
        },
        0,
        fallbackContext,
        currentUser.id
      );

      if (!entry) return;

      set((s) => {
        const feedback = normalizeFeedbackList(
          [...(s.feedback || []), entry],
          fallbackContext,
          currentUser.id
        );

        let projectsById = s.projects?.byId || {};
        const activeProjectId = activeRefs?.activeProjectId || null;
        if (activeProjectId && projectsById[activeProjectId]) {
          const proj = normalizeProject(projectsById[activeProjectId]);
          if (proj) {
            const activityEntry = makeActivityEntry({
              entityType: "overview",
              entityId: activeProjectId,
              initiativeId: activeRefs?.activeInitiativeId || null,
              action: "created",
              actor: "user",
              timestamp: entry.createdAt,
              shortDescription: `Feedback submitted: ${entry.type}`
            });

            const nextProj = normalizeProject({
              ...appendActivity(proj, activityEntry),
              meta: { ...(proj.meta || {}), updatedAt: now() }
            });

            projectsById = { ...projectsById, [activeProjectId]: nextProj };
          }
        }

        return {
          feedback,
          projects: { ...s.projects, byId: projectsById }
        };
      });
    },

    updateFeedbackStatus(id, status) {
      if (!id || !FEEDBACK_STATUSES.has(status)) return;
      set((s) => {
        const next = normalizeFeedbackList(
          (s.feedback || []).map((item) =>
          item.id === id ? { ...item, status } : item
          ),
          null,
          s.currentUser?.id
        );
        return { feedback: next };
      });
    },

    deleteFeedback(id) {
      if (!id) return;
      set((s) => {
        const next = normalizeFeedbackList(
          (s.feedback || []).filter((item) => item.id !== id),
          null,
          s.currentUser?.id
        );
        return { feedback: next };
      });
    },

    exportFeedback() {
      const list = normalizeFeedbackList(get().feedback, null, get().currentUser?.id);
      return JSON.stringify(list, null, 2);
    },

    logActivity(payload) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId) return;

      const entry = makeActivityEntry(payload);
      if (!entry) return;

      set((s) => {
        const proj = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj) return s;

        const nextProj = normalizeProject({
          ...appendActivity(proj, entry),
          meta: { ...(proj.meta || {}), updatedAt: now() }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });
    },

    selectActiveRefs() {
      return buildStableActiveSnapshot(get());
    },

    getActiveProject() {
      return get().selectActiveRefs()?.project || null;
    },

    getActiveInitiative() {
      return get().selectActiveRefs()?.initiative || null;
    },

    getActiveArtifact(key) {
      const refs = get().selectActiveRefs();
      const init = refs?.initiative || null;
      if (!init) return null;
      if (ARTIFACT_KEYS.has(key)) return init?.[key] ?? null;
      return null;
    },

    syncActiveProjectSnapshot() {
      invalidateActiveSnapshotCache();
    },

    initializeIfEmpty() {
      const s = get();
      if (s.workspaces?.allIds?.length) return;
      if (s.projects?.allIds?.length) return;
      if (s.activeWorkspaceId || s.activeProjectByWorkspaceId) return;

      const wsId = uid("ws");
      const pId = uid("proj");

      const workspace = {
        id: wsId,
        name: "My workspace",
        createdAt: now(),
        updatedAt: now()
      };

      const project = makeEmptyProject({
        id: pId,
        workspaceId: wsId,
        projectName: "Untitled project",
        projectType: "new",
        parentProjectId: null
      });

      const normalized = normalizeProject(project);
      const initId =
      normalized?.initiatives?.activeId || normalized?.initiatives?.allIds?.[0] || null;

      set({
        workspaces: { byId: { [wsId]: workspace }, allIds: [wsId] },
        activeWorkspaceId: wsId,
        projects: { byId: { [pId]: normalized }, allIds: [pId] },
        activeProjectByWorkspaceId: { [wsId]: pId },
        activeInitiativeByProjectId: initId ? { [pId]: initId } : {},
        researchFocus: null,
        targetInsightId: null
      });

      invalidateActiveSnapshotCache();
    },

    loadSnapshot(snapshot) {
      if (!snapshot || typeof snapshot !== "object") return;
      const proj = normalizeProject(snapshot);
      if (!proj) return;

      const wsId = proj?.meta?.workspaceId || uid("ws");
      const projectId = proj.id;
      const initId = proj?.initiatives?.activeId || proj?.initiatives?.allIds?.[0] || null;

      set((s) => {
        const workspacesById = { ...(s.workspaces?.byId || {}) };
        const workspacesAllIds = Array.isArray(s.workspaces?.allIds) ? s.workspaces.allIds.slice() : [];

        if (!workspacesById[wsId]) {
          workspacesById[wsId] = {
            id: wsId,
            name: "My workspace",
            createdAt: now(),
            updatedAt: now()
          };
          workspacesAllIds.push(wsId);
        }

        const projectsById = { ...(s.projects?.byId || {}), [projectId]: proj };
        const projectsAllIds = Array.isArray(s.projects?.allIds) ? s.projects.allIds.slice() : [];
        if (!projectsAllIds.includes(projectId)) projectsAllIds.push(projectId);

        return {
          workspaces: { byId: workspacesById, allIds: workspacesAllIds },
          activeWorkspaceId: wsId,
          projects: { byId: projectsById, allIds: projectsAllIds },
          activeProjectByWorkspaceId: { ...(s.activeProjectByWorkspaceId || {}), [wsId]: projectId },
          activeInitiativeByProjectId: initId ?
          { ...(s.activeInitiativeByProjectId || {}), [projectId]: initId } :
          { ...(s.activeInitiativeByProjectId || {}) },
          researchFocus: null,
          targetInsightId: null
        };
      });

      invalidateActiveSnapshotCache();
    },

    mergeProjectSnapshot(snapshot) {
      if (!snapshot || typeof snapshot !== "object") return;
      const proj = normalizeProject(snapshot);
      if (!proj) return;

      set((s) => {
        const existing = normalizeProject(s?.projects?.byId?.[proj.id]);
        const effectiveProject = normalizeProject(
          mergeProjectSnapshotsByTimestamp(proj, existing)
        );
        if (!effectiveProject) return s;
        const wsId = effectiveProject?.meta?.workspaceId || s.activeWorkspaceId || uid("ws");
        const initId =
        effectiveProject?.initiatives?.activeId ||
        effectiveProject?.initiatives?.allIds?.[0] ||
        null;

        const workspacesById = { ...(s.workspaces?.byId || {}) };
        const workspacesAllIds = Array.isArray(s.workspaces?.allIds) ? s.workspaces.allIds.slice() : [];
        if (!workspacesById[wsId]) {
          workspacesById[wsId] = { id: wsId, name: "My workspace", createdAt: now(), updatedAt: now() };
          workspacesAllIds.push(wsId);
        }

        const projectsById = { ...(s.projects?.byId || {}), [effectiveProject.id]: effectiveProject };
        const projectsAllIds = Array.isArray(s.projects?.allIds) ? s.projects.allIds.slice() : [];
        if (!projectsAllIds.includes(effectiveProject.id)) projectsAllIds.push(effectiveProject.id);

        const activeWorkspaceId = s.activeWorkspaceId || wsId;
        const activeProjectByWorkspaceId = { ...(s.activeProjectByWorkspaceId || {}) };
        if (!activeProjectByWorkspaceId[wsId]) activeProjectByWorkspaceId[wsId] = effectiveProject.id;

        const activeInitiativeByProjectId = { ...(s.activeInitiativeByProjectId || {}) };
        if (initId && !activeInitiativeByProjectId[effectiveProject.id]) {
          activeInitiativeByProjectId[effectiveProject.id] = initId;
        }

        return {
          workspaces: { byId: workspacesById, allIds: workspacesAllIds },
          activeWorkspaceId,
          projects: { byId: projectsById, allIds: projectsAllIds },
          activeProjectByWorkspaceId,
          activeInitiativeByProjectId
        };
      });

      invalidateActiveSnapshotCache();
    },

    reset() {
      set({
        schemaVersion: 3,
        workspaces: { byId: {}, allIds: [] },
        activeWorkspaceId: null,
        projects: { byId: {}, allIds: [] },
        activeProjectByWorkspaceId: {},
        activeInitiativeByProjectId: {},
        researchFocus: null,
        targetInsightId: null
      });
      invalidateActiveSnapshotCache();
    },

    createWorkspace(name = "New workspace") {
      const wsId = uid("ws");
      const pId = uid("proj");

      const workspace = { id: wsId, name, createdAt: now(), updatedAt: now() };

      const project = normalizeProject(
        makeEmptyProject({
          id: pId,
          workspaceId: wsId,
          projectName: "Untitled project",
          projectType: "new",
          parentProjectId: null
        })
      );

      const initId = project?.initiatives?.activeId || project?.initiatives?.allIds?.[0] || null;

      set((s) => ({
        workspaces: {
          byId: { ...(s.workspaces?.byId || {}), [wsId]: workspace },
          allIds: [...(s.workspaces?.allIds || []), wsId]
        },
        projects: {
          byId: { ...(s.projects?.byId || {}), [pId]: project },
          allIds: [...(s.projects?.allIds || []), pId]
        },
        activeWorkspaceId: wsId,
        activeProjectByWorkspaceId: { ...(s.activeProjectByWorkspaceId || {}), [wsId]: pId },
        activeInitiativeByProjectId: initId ?
        { ...(s.activeInitiativeByProjectId || {}), [pId]: initId } :
        { ...(s.activeInitiativeByProjectId || {}) },
        researchFocus: null,
        targetInsightId: null
      }));

      invalidateActiveSnapshotCache();
      return wsId;
    },

    renameWorkspace(workspaceId, name) {
      set((s) => {
        const w = s.workspaces?.byId?.[workspaceId];
        if (!w) return s;
        return {
          workspaces: {
            ...s.workspaces,
            byId: {
              ...s.workspaces.byId,
              [workspaceId]: { ...w, name: safeText(name).trim() || w.name, updatedAt: now() }
            }
          }
        };
      });
    },

    setActiveWorkspace(workspaceId) {
      const { workspaces } = get();
      if (!workspaces?.byId?.[workspaceId]) return;
      set({ activeWorkspaceId: workspaceId, researchFocus: null, targetInsightId: null });
      invalidateActiveSnapshotCache();
    },

    createProjectInActiveWorkspace({
      projectName = "Untitled project",
      projectType = "new",
      productDescription = "",
      parentProjectId = null
    } = {}) {
      const wsId = get().activeWorkspaceId;
      if (!wsId) return null;

      const pId = uid("proj");
      const project = normalizeProject(
        makeEmptyProject({
          id: pId,
          workspaceId: wsId,
          projectName,
          projectType,
          productDescription,
          parentProjectId
        })
      );

      const initId = project?.initiatives?.activeId || project?.initiatives?.allIds?.[0] || null;

      set((s) => ({
        projects: {
          byId: { ...(s.projects?.byId || {}), [pId]: project },
          allIds: [...(s.projects?.allIds || []), pId]
        },
        activeProjectByWorkspaceId: { ...(s.activeProjectByWorkspaceId || {}), [wsId]: pId },
        activeInitiativeByProjectId: initId ?
        { ...(s.activeInitiativeByProjectId || {}), [pId]: initId } :
        { ...(s.activeInitiativeByProjectId || {}) },
        researchFocus: null,
        targetInsightId: null
      }));

      invalidateActiveSnapshotCache();
      return pId;
    },

    deleteProject(projectId) {
      const pid = safeText(projectId).trim();
      if (!pid) return;
      set((s) => {
        const byId = { ...(s.projects?.byId || {}) };
        if (!byId[pid]) return s;
        delete byId[pid];

        const allIds = (s.projects?.allIds || []).filter((id) => id !== pid);
        const activeByWs = { ...(s.activeProjectByWorkspaceId || {}) };
        const activeInitByProj = { ...(s.activeInitiativeByProjectId || {}) };
        delete activeInitByProj[pid];

        Object.keys(activeByWs).forEach((wsId) => {
          if (activeByWs[wsId] !== pid) return;
          const nextId = allIds.find((id) => {
            const proj = byId[id];
            const workspaceId = proj?.meta?.workspaceId || proj?.workspaceId || "";
            return String(workspaceId) === String(wsId);
          });
          if (nextId) activeByWs[wsId] = nextId;else
          delete activeByWs[wsId];
        });

        return {
          projects: { byId, allIds },
          activeProjectByWorkspaceId: activeByWs,
          activeInitiativeByProjectId: activeInitByProj
        };
      });
      invalidateActiveSnapshotCache();
    },

    setActiveProject(projectId) {
      const p = get().projects?.byId?.[projectId];
      if (!p) return;
      const currentWsId = String(get().activeWorkspaceId || "").trim();
      const projectWsId = String(p?.meta?.workspaceId || p?.workspaceId || "").trim();
      const wsId = projectWsId || currentWsId;
      if (!wsId) return;

      const normalized = normalizeProject(p);
      const initId =
      (get().activeInitiativeByProjectId || {})[projectId] ||
      normalized?.initiatives?.activeId ||
      normalized?.initiatives?.allIds?.[0] ||
      null;

      set((s) => ({
        activeWorkspaceId: wsId,
        activeProjectByWorkspaceId: { ...(s.activeProjectByWorkspaceId || {}), [wsId]: projectId },
        activeInitiativeByProjectId: initId ?
        { ...(s.activeInitiativeByProjectId || {}), [projectId]: initId } :
        { ...(s.activeInitiativeByProjectId || {}) },
        projects: { ...s.projects, byId: { ...(s.projects?.byId || {}), [projectId]: normalized } },
        researchFocus: null,
        targetInsightId: null
      }));

      invalidateActiveSnapshotCache();
    },

    setMeta(metaPatch) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId) return;

      set((s) => {
        const proj = normalizeProject(s.projects?.byId?.[activeProjectId]);
        if (!proj) return s;

        const next = normalizeProject({
          ...proj,
          meta: { ...(proj.meta || {}), ...(metaPatch || {}), updatedAt: now() }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: next } } };
      });

      invalidateActiveSnapshotCache();
    },
    setResearchItems(items) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId) return;

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const normalizedItems = normalizeResearchItems(items);

        const nextInsights = normalizeInsightsContainer(currentInit.researchInsights, normalizedItems);
        const nextResearchVersion = bumpVersion(getResearchVersion(currentInit), normalizedItems.length > 0 ? 1 : 0);

        const nextInit = {
          ...currentInit,
          researchItems: normalizedItems,
          researchInsights: nextInsights,
          researchVersion: nextResearchVersion,
          researchUpdatedAt: now(),
          researchUpdatedBy: resolveUserLabel(s),
          updatedAt: now()
        };

        const nextProj = normalizeProject({
          ...proj,
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: now() }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });

      invalidateActiveSnapshotCache();
    },

    setResearchLastAIGenerationAt(ts) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId) return;

      set((s) => {
        const proj = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj0, initId } = ensureActiveInitiativeForProject(proj, mappedInitId);
        if (!initId) return s;

        const currentInit = proj0.initiatives.byId[initId];
        const nextInit = { ...currentInit, researchLastAIGenerationAt: ts ?? null, updatedAt: now() };

        const nextProj = normalizeProject({
          ...proj0,
          initiatives: {
            ...proj0.initiatives,
            byId: { ...proj0.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj0.meta || {}), updatedAt: now() }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });

      invalidateActiveSnapshotCache();
    },

    setInsightsLastGeneratedAt(ts, actor = "AI") {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId) return;

      set((s) => {
        const proj = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj0, initId } = ensureActiveInitiativeForProject(proj, mappedInitId);
        if (!initId) return s;

        const currentInit = proj0.initiatives.byId[initId];
        let nextInit = {
          ...currentInit,
          insightsLastGeneratedAt: ts ?? null,
          insightsEditedAt: ts ?? now(),
          insightsEditedBy: actor,
          updatedAt: now()
        };
        nextInit = markInsightsApprovalStaleIfNeeded(nextInit, true);

        const nextProj = normalizeProject({
          ...proj0,
          initiatives: {
            ...proj0.initiatives,
            byId: { ...proj0.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj0.meta || {}), updatedAt: now() }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });

      invalidateActiveSnapshotCache();
    },

    setDownstreamComputedAt(ts) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId) return;

      set((s) => {
        const proj = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj0, initId } = ensureActiveInitiativeForProject(proj, mappedInitId);
        if (!initId) return s;

        const currentInit = proj0.initiatives.byId[initId];
        const nextInit = {
          ...currentInit,
          downstreamComputedAt: ts ?? null,
          updatedAt: now()
        };

        const nextProj = normalizeProject({
          ...proj0,
          initiatives: {
            ...proj0.initiatives,
            byId: { ...proj0.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj0.meta || {}), updatedAt: now() }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });

      invalidateActiveSnapshotCache();
    },

    setDismissedForResearchVersion(version) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId) return;

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const nextInit = {
          ...currentInit,
          dismissedForResearchVersion: normalizeVersion(version, getResearchVersion(currentInit)),
          updatedAt: now()
        };

        const nextProj = normalizeProject({
          ...proj,
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: now() }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });

      invalidateActiveSnapshotCache();
    },

    setDismissedArtifactFreshnessForInsightsVersion(artifactType, version) {
      const key = String(artifactType || "").trim();
      if (!key) return;
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId) return;

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const normalizedInsightsVersion = normalizeVersion(version, normalizeVersion(currentInit?.insightsVersion, 0));
        const currentDismissedMap =
        currentInit?.dismissedArtifactFreshnessByInsightsVersion &&
        typeof currentInit.dismissedArtifactFreshnessByInsightsVersion === "object" ?
        currentInit.dismissedArtifactFreshnessByInsightsVersion :
        {};
        const nextDismissedMap = {
          ...currentDismissedMap,
          [key]: normalizedInsightsVersion
        };

        const nextInit = {
          ...currentInit,
          dismissedArtifactFreshnessByInsightsVersion: nextDismissedMap,
          updatedAt: now()
        };

        const nextProj = normalizeProject({
          ...proj,
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: now() }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });

      invalidateActiveSnapshotCache();
    },

    setDismissedDecisionStaleSignature(signature) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId) return;

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const nextInit = {
          ...currentInit,
          dismissedDecisionStaleSignature:
          typeof signature === "string" && signature.trim() ? signature.trim() : null,
          updatedAt: now()
        };

        const nextProj = normalizeProject({
          ...proj,
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: now() }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });

      invalidateActiveSnapshotCache();
    },

    setFromAI(generatedState) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId || !generatedState) return;

      invalidateActiveSnapshotCache();

      const tIso = isoNow();

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const nextProjectPatch = { ...proj };

        if (generatedState.meta) {
          nextProjectPatch.meta = { ...(proj.meta || {}), ...(generatedState.meta || {}) };
        }

        // project-level personas (per your current model)
        if (Array.isArray(generatedState.personas)) {
          nextProjectPatch.personas = generatedState.personas;
        }
        if (generatedState.primaryPersonaId !== undefined) {
          nextProjectPatch.primaryPersonaId = generatedState.primaryPersonaId ?? null;
        }

        const currentInit = proj.initiatives.byId[initId];
        let nextInit = { ...currentInit, updatedAt: now() };
        const activityEntries = [];
        const aiUpdatedInsights =
        generatedState.researchInsights && typeof generatedState.researchInsights === "object";

        if (Array.isArray(generatedState.researchItems)) {
          nextInit.researchItems = normalizeResearchItems(generatedState.researchItems);
          nextInit.researchUpdatedAt = now();
          nextInit.researchUpdatedBy = "AI";
        }

        if (generatedState.researchInsights && typeof generatedState.researchInsights === "object") {
          nextInit.researchInsights = normalizeInsightsContainer(generatedState.researchInsights, nextInit.researchItems);
        } else {
          nextInit.researchInsights = normalizeInsightsContainer(nextInit.researchInsights, nextInit.researchItems);
        }

        if (generatedState.researchInsights && typeof generatedState.researchInsights === "object") {
          const rawSignals = generatedState.researchInsights.emergingSignals ?? generatedState.emergingSignals;
          if (rawSignals !== undefined) {
            const emergingSignals = Array.isArray(rawSignals) ?
            { items: rawSignals } :
            rawSignals && typeof rawSignals === "object" ?
            rawSignals :
            { items: [] };
            nextInit.researchInsights = { ...nextInit.researchInsights, emergingSignals };
          }
        }

        if (aiUpdatedInsights) {
          const aiItems = (nextInit.researchInsights?.items || []).map((it) => {
            const createdBy =
            typeof it.createdBy === "string" && it.createdBy.trim() ?
            it.createdBy.trim() :
            it.provenance?.createdBy === "ai" ?
            "ai" :
            "local-user";
            return { ...it, createdBy, updatedBy: "ai", updatedAt: tIso };
          });
          nextInit.researchInsights = { ...nextInit.researchInsights, items: aiItems, updatedAt: now() };
          nextInit.insightsLastGeneratedAt = now();
          nextInit.insightsEditedAt = now();
          nextInit.insightsEditedBy = "AI";
          nextInit = markInsightsApprovalStaleIfNeeded(nextInit, true);

          aiItems.forEach((it) => {
            activityEntries.push(
              makeActivityEntry({
                entityType: "insight",
                entityId: it.id,
                initiativeId: initId,
                action: "regenerated",
                actor: "ai",
                timestamp: tIso,
                shortDescription: "Insight regenerated by AI."
              })
            );
          });
        }

        const didUpdateInsightGroups =
        generatedState.insightGroups && typeof generatedState.insightGroups === "object";
        if (didUpdateInsightGroups) {
          nextInit.insightGroups = normalizeInsightGroups(generatedState.insightGroups);
        } else {
          nextInit.insightGroups = normalizeInsightGroups(nextInit.insightGroups);
        }
        if (didUpdateInsightGroups && !aiUpdatedInsights) {
          nextInit = touchInsightsDraft(nextInit, "AI");
        }

        if (generatedState.researchTagSpans && typeof generatedState.researchTagSpans === "object") {
          nextInit.researchTagSpans = normalizeResearchTagSpans(generatedState.researchTagSpans);
        } else {
          nextInit.researchTagSpans = normalizeResearchTagSpans(nextInit.researchTagSpans);
        }

        if (generatedState.atomicFacts && typeof generatedState.atomicFacts === "object") {
          nextInit.atomicFacts = normalizeAtomicFacts(generatedState.atomicFacts, nextInit.researchItems.length);
        } else {
          nextInit.atomicFacts = normalizeAtomicFacts(nextInit.atomicFacts, nextInit.researchItems.length);
        }

        if (generatedState.tagVocabulary && typeof generatedState.tagVocabulary === "object") {
          nextInit.tagVocabulary = normalizeTagVocabulary(generatedState.tagVocabulary);
        } else {
          nextInit.tagVocabulary = normalizeTagVocabulary(nextInit.tagVocabulary);
        }

        if (generatedState.tagThemes && typeof generatedState.tagThemes === "object") {
          nextInit.tagThemes = mergeTagThemesWithAI(nextInit.tagThemes, generatedState.tagThemes);
        } else {
          nextInit.tagThemes = normalizeTagThemes(nextInit.tagThemes);
        }

        if (Array.isArray(generatedState.decisions)) {
          nextInit.decisions = normalizeDecisions(generatedState.decisions, getDecisionInsights(nextInit));
        }

        if (generatedState.direction && typeof generatedState.direction === "object") {
          nextInit.direction = generatedState.direction;
        }

        if (generatedState.problemDefinition && typeof generatedState.problemDefinition === "object") {
          const currentProblemDef =
          nextInit?.problemDefinition && typeof nextInit.problemDefinition === "object" ?
          nextInit.problemDefinition :
          {};
          const problemGeneratedAt = now();
          nextInit.problemDefinition = {
            ...currentProblemDef,
            ...generatedState.problemDefinition,
            generatedAt: Number.isFinite(Number(generatedState.problemDefinition.generatedAt)) ?
            Number(generatedState.problemDefinition.generatedAt) :
            problemGeneratedAt,
            updatedAt: problemGeneratedAt,
            seededFromProjectGeneration: Boolean(generatedState.initialProblemDefinitionSeed) ||
            Boolean(currentProblemDef.seededFromProjectGeneration)
          };
        } else if (
        typeof generatedState.problemDefinition === "string" ||
        typeof generatedState.weWill === "string")
        {
          const currentProblemDef =
          nextInit?.problemDefinition && typeof nextInit.problemDefinition === "object" ?
          nextInit.problemDefinition :
          {};
          const nextProblem = safeText(generatedState.problemDefinition || "").trim();
          const nextWeWill = safeText(generatedState.weWill || "").trim();
          if (nextProblem || nextWeWill) {
            const problemGeneratedAt = now();
            nextInit.problemDefinition = {
              ...currentProblemDef,
              problem: nextProblem || safeText(currentProblemDef.problem || "").trim(),
              weWill: nextWeWill || safeText(currentProblemDef.weWill || "").trim(),
              generatedAt: problemGeneratedAt,
              updatedAt: problemGeneratedAt,
              seededFromProjectGeneration: Boolean(currentProblemDef.seededFromProjectGeneration)
            };
          }
        }

        const handledKeys = new Set([
        "researchItems",
        "researchInsights",
        "insightGroups",
        "researchTagSpans",
        "atomicFacts",
        "tagVocabulary",
        "tagThemes",
        "decisions",
        "direction",
        "problemDefinition",
        "weWill"]
        );

        for (const key of ARTIFACT_KEYS) {
          if (handledKeys.has(key)) continue;
          if (generatedState[key] !== undefined) nextInit[key] = generatedState[key];
        }

        // Keep tag vocab/themes aligned with all known tag sources after AI updates.
        const aligned = applyTagThemeInvariants({
          tagVocabulary: nextInit.tagVocabulary,
          tagThemes: nextInit.tagThemes,
          researchTagSpans: nextInit.researchTagSpans,
          researchInsights: nextInit.researchInsights,
          includeThemeTags: false,
          includeThemeOverrides: false
        });
        nextInit.tagVocabulary = aligned.tagVocabulary;
        nextInit.tagThemes = aligned.tagThemes;

        const researchVersion = getResearchVersion(nextInit);
        let nextInitWithMeta = nextInit;
        if (generatedState.researchInsights && typeof generatedState.researchInsights === "object") {
          nextInitWithMeta = markInsightsDerived(nextInitWithMeta, researchVersion);
        }
        if (
        generatedState.researchTagSpans && typeof generatedState.researchTagSpans === "object" ||
        generatedState.tagVocabulary && typeof generatedState.tagVocabulary === "object" ||
        generatedState.tagThemes && typeof generatedState.tagThemes === "object")
        {
          nextInitWithMeta = markTagsDerived(nextInitWithMeta, researchVersion);
        }

        const skipInsightGrouping = generatedState?.skipInsightGrouping === true;
        if (skipInsightGrouping) {
          const forcedUngrouped = (nextInitWithMeta?.researchInsights?.items || []).map((it) => ({
            ...it,
            groupId: "grp_ungrouped"
          }));
          nextInitWithMeta.insightGroups = normalizeInsightGroups({
            groups: [{ id: "grp_ungrouped", title: "Ungrouped insights", order: 0 }]
          });
          nextInitWithMeta.researchInsights = {
            ...nextInitWithMeta.researchInsights,
            items: forcedUngrouped
          };
        } else {
          const synced = syncInsightGroups(
            nextInitWithMeta.insightGroups,
            nextInitWithMeta.researchInsights.items,
            nextInitWithMeta.tagThemes
          );
          nextInitWithMeta.insightGroups = synced.groups;
          nextInitWithMeta.researchInsights = { ...nextInitWithMeta.researchInsights, items: synced.items };
        }
        nextInitWithMeta.decisions = normalizeDecisions(currentInit.decisions, getDecisionInsights(nextInitWithMeta));

        if (generatedState.researchInsights && typeof generatedState.researchInsights === "object") {
          const rawSignals = generatedState.researchInsights.emergingSignals ?? generatedState.emergingSignals;
          if (rawSignals !== undefined) {
            const emergingSignals = Array.isArray(rawSignals) ?
            { items: rawSignals } :
            rawSignals && typeof rawSignals === "object" ?
            rawSignals :
            { items: [] };
            nextInitWithMeta.researchInsights = { ...nextInitWithMeta.researchInsights, emergingSignals };
          }
        }

        const decisionSync = reconcileDecisionsWithInsights(
          nextInitWithMeta.decisions,
          getDecisionInsights(nextInitWithMeta),
          initId
        );
        nextInitWithMeta.decisions = decisionSync.decisions;
        activityEntries.push(...(decisionSync.activityEntries || []));

        const hasDownstreamOutputs = Boolean(
          generatedState.overview ||
          generatedState.personas ||
          generatedState.journeys ||
          generatedState.problemDefinition ||
          generatedState.direction
        );
        if (hasDownstreamOutputs) {
          nextInitWithMeta.downstreamComputedAt = now();
        }

        if (DEBUG_AI_TAGS) {
          void 0;
          void 0;
          void 0;
          void 0;
          void 0;
          void 0;
          void 0;
          void 0;
          void 0;
          void 0;
          void 0;
          void 0;



          void 0;



          void 0;
        }

        const insightsSets = {
          ...(currentInit.insightsSets && typeof currentInit.insightsSets === "object" ? currentInit.insightsSets : {}),
          auto: {
            researchInsights: nextInitWithMeta.researchInsights,
            insightGroups: nextInitWithMeta.insightGroups,
            updatedAt: now()
          }
        };
        nextInitWithMeta.insightsSets = insightsSets;
        nextInitWithMeta.activeInsightsSet = "auto";

        const nextProj = normalizeProject({
          ...appendActivity(nextProjectPatch, activityEntries.filter(Boolean)),
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInitWithMeta },
            activeId: initId
          },
          meta: { ...(nextProjectPatch.meta || {}), updatedAt: now() }
        });

        return {
          projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } },
          activeInitiativeByProjectId: initId ?
          { ...(s.activeInitiativeByProjectId || {}), [activeProjectId]: initId } :
          { ...(s.activeInitiativeByProjectId || {}) },
          researchFocus: null,
          targetInsightId: null
        };
      });

      invalidateActiveSnapshotCache();
    },

    setActiveInitiative(initiativeId) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId) return;

      set((s) => {
        const proj = normalizeProject(s.projects.byId[activeProjectId]);
        const exists = !!proj?.initiatives?.byId?.[initiativeId];
        if (!exists) return s;

        const nextProj = normalizeProject({
          ...proj,
          initiatives: { ...proj.initiatives, activeId: initiativeId },
          meta: { ...(proj.meta || {}), updatedAt: now() }
        });

        return {
          projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } },
          activeInitiativeByProjectId: { ...(s.activeInitiativeByProjectId || {}), [activeProjectId]: initiativeId },
          researchFocus: null,
          targetInsightId: null
        };
      });

      invalidateActiveSnapshotCache();
    },

    createInitiative({ name, description, aiBrief, goals, successMetrics, risks, linkedInsightIds } = {}) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId) return null;

      const nameText = safeText(name).trim();
      if (!nameText) return null;

      const initId = uid("init");
      const t = now();

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const nextInit = {
          id: initId,
          name: nameText,
          description: safeText(description).trim(),
          aiBrief: safeText(aiBrief).trim(),
          goals: normalizeStringArray(goals),
          successMetrics: normalizeStringArray(successMetrics),
          risks: normalizeStringArray(risks),
          linkedInsightIds: normalizeStringArray(linkedInsightIds),
          projectId: activeProjectId,
          createdAt: t,
          updatedAt: t
        };

        const nextProj = normalizeProject({
          ...proj0,
          initiatives: {
            ...proj0.initiatives,
            byId: { ...proj0.initiatives.byId, [initId]: nextInit },
            allIds: [...(proj0.initiatives.allIds || []), initId]
          },
          meta: { ...(proj0.meta || {}), updatedAt: t }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });

      invalidateActiveSnapshotCache();
      return initId;
    },

    assignDecisionsToInitiative({ decisionIds, initiativeId } = {}) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId) return;

      const targetInitId = typeof initiativeId === "string" && initiativeId.trim() ? initiativeId.trim() : null;
      if (!targetInitId) return;

      const idSet = new Set(
        Array.isArray(decisionIds) ? decisionIds.map((id) => typeof id === "string" ? id.trim() : "").filter(Boolean) : []
      );
      if (!idSet.size) return;

      invalidateActiveSnapshotCache();

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        if (!proj0?.initiatives?.byId?.[targetInitId]) return s;

        const nextById = { ...proj0.initiatives.byId };
        const moved = [];

        (proj0.initiatives.allIds || []).forEach((initId) => {
          const currentInit = nextById[initId];
          if (!currentInit) return;

          const remaining = [];
          (currentInit.decisions || []).forEach((dec) => {
            if (!dec || !dec.id) return;
            if (idSet.has(dec.id)) {
              moved.push({ ...dec, initiativeId: targetInitId, updatedAt: isoNow() });
            } else {
              remaining.push(dec);
            }
          });

          if (remaining.length !== (currentInit.decisions || []).length) {
            nextById[initId] = {
              ...currentInit,
              decisions: normalizeDecisions(remaining, getDecisionInsights(currentInit)),
              updatedAt: now()
            };
          }
        });

        if (!moved.length) return s;

        const targetInit = nextById[targetInitId];
        const targetDecisions = [...(targetInit.decisions || [])];
        const existingIds = new Set(targetDecisions.map((d) => d.id));
        moved.forEach((dec) => {
          if (!existingIds.has(dec.id)) targetDecisions.push(dec);
        });

        nextById[targetInitId] = {
          ...targetInit,
          decisions: normalizeDecisions(targetDecisions, getDecisionInsights(targetInit)),
          updatedAt: now()
        };

        const nextProj = normalizeProject({
          ...proj0,
          initiatives: { ...proj0.initiatives, byId: nextById },
          meta: { ...(proj0.meta || {}), updatedAt: now() }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });
    },

    setArtifact(artifactKey, value) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId || !ARTIFACT_KEYS.has(artifactKey)) return;

      invalidateActiveSnapshotCache();

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        let nextInit = { ...currentInit, updatedAt: now() };

        if (artifactKey === "researchInsights") {
          nextInit.researchInsights = normalizeInsightsContainer(value, nextInit.researchItems);
          nextInit.insightsEditedAt = now();
          nextInit.insightsEditedBy = resolveUserLabel(s);
        } else if (artifactKey === "researchTagSpans") {
          nextInit.researchTagSpans = normalizeResearchTagSpans(value);
        } else if (artifactKey === "insightGroups") {
          nextInit.insightGroups = normalizeInsightGroups(value);
          nextInit = touchInsightsDraft(nextInit, resolveUserLabel(s));
        } else if (artifactKey === "tagVocabulary") {
          nextInit.tagVocabulary = normalizeTagVocabulary(value);
        } else if (artifactKey === "tagThemes") {
          nextInit.tagThemes = normalizeTagThemes(value);
        } else if (artifactKey === "storyArtifacts") {
          nextInit.storyArtifacts = normalizeStoryArtifacts(value);
        } else if (artifactKey === "problemDefinition" && value && typeof value === "object") {
          nextInit.problemDefinition = {
            ...value,
            generatedAt:
              Number.isFinite(Number(value.generatedAt)) && Number(value.generatedAt) > 0 ?
              Number(value.generatedAt) :
              now(),
            seededFromProjectGeneration: false,
          };
        } else {
          nextInit[artifactKey] = value;
        }

        if (
        artifactKey === "tagVocabulary" ||
        artifactKey === "tagThemes" ||
        artifactKey === "researchTagSpans" ||
        artifactKey === "researchInsights")
        {
          // Keep tag vocab/themes aligned whenever tag sources change.
          const aligned = applyTagThemeInvariants({
            tagVocabulary: nextInit.tagVocabulary,
            tagThemes: nextInit.tagThemes,
            researchTagSpans: nextInit.researchTagSpans,
            researchInsights: nextInit.researchInsights
          });
          nextInit.tagVocabulary = aligned.tagVocabulary;
          nextInit.tagThemes = aligned.tagThemes;
        }

        const researchVersion = getResearchVersion(nextInit);
        if (artifactKey === "researchInsights") {
          nextInit = markInsightsDerived(nextInit, researchVersion);
        }
        if (artifactKey === "researchInsights" || artifactKey === "insightGroups") {
          nextInit = syncActiveInsightsSet(nextInit);
        }

        if (
        artifactKey === "tagVocabulary" ||
        artifactKey === "tagThemes" ||
        artifactKey === "researchTagSpans")
        {
          nextInit = markTagsDerived(nextInit, researchVersion);
        }

        const synced = syncInsightGroups(
          nextInit.insightGroups,
          nextInit.researchInsights.items,
          nextInit.tagThemes
        );
        nextInit.insightGroups = synced.groups;
        nextInit.researchInsights = { ...nextInit.researchInsights, items: synced.items };
        let activityEntries = [];

        if (artifactKey === "researchInsights") {
          const decisionSync = reconcileDecisionsWithInsights(
            currentInit.decisions,
            getDecisionInsights(currentInit),
            initId
          );
          nextInit.decisions = decisionSync.decisions;
          activityEntries = decisionSync.activityEntries || [];
          if (nextInit.insightsApprovalStatus === "approved") {
            nextInit.insightsApprovalStatus = "stale";
          }
        }

        const nextProj = normalizeProject({
          ...appendActivity(proj, activityEntries.filter(Boolean)),
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: now() }
        });

        return {
          projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } },
          activeInitiativeByProjectId: { ...(s.activeInitiativeByProjectId || {}), [activeProjectId]: initId }
        };
      });

      invalidateActiveSnapshotCache();
    },

    getEffectiveTagThemes() {
      const init = get().getActiveInitiative();
      if (!init) return normalizeTagThemes(null);
      const knownTags = collectKnownTags({
        tagVocabulary: init.tagVocabulary,
        researchTagSpans: init.researchTagSpans,
        researchInsights: init.researchInsights,
        tagThemes: init.tagThemes
      });
      return buildEffectiveTagThemes(init.tagThemes, knownTags);
    },

    setTagThemesFromAI(payload) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId || !payload) return;

      invalidateActiveSnapshotCache();

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const nextInit = { ...currentInit, updatedAt: now() };

        const incomingTagThemes = payload?.tagThemes ?? payload;
        if (incomingTagThemes && typeof incomingTagThemes === "object") {
          nextInit.tagThemes = mergeTagThemesWithAI(currentInit.tagThemes, incomingTagThemes);
        }

        if (payload?.tagVocabulary && typeof payload.tagVocabulary === "object") {
          nextInit.tagVocabulary = normalizeTagVocabulary(payload.tagVocabulary);
        } else {
          nextInit.tagVocabulary = normalizeTagVocabulary(nextInit.tagVocabulary);
        }

        // Keep tag vocab/themes aligned after AI updates.
        const aligned = applyTagThemeInvariants({
          tagVocabulary: nextInit.tagVocabulary,
          tagThemes: nextInit.tagThemes,
          researchTagSpans: nextInit.researchTagSpans,
          researchInsights: nextInit.researchInsights,
          includeThemeTags: false,
          includeThemeOverrides: false
        });
        nextInit.tagVocabulary = aligned.tagVocabulary;
        nextInit.tagThemes = aligned.tagThemes;

        const researchVersion = getResearchVersion(nextInit);
        const nextInitWithMeta = markTagsDerived(nextInit, researchVersion);

        if (DEBUG_AI_TAGS) {
          void 0;
          void 0;
        }

        const nextProj = normalizeProject({
          ...proj,
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInitWithMeta },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: now() }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });

      invalidateActiveSnapshotCache();
    },

    renameTagTheme(themeId, newTitle) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId || !themeId || themeId === UNASSIGNED_THEME_ID) return;

      invalidateActiveSnapshotCache();

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const cont = normalizeTagThemes(currentInit.tagThemes);
        const t = now();
        const safeTitle = safeText(newTitle).trim();

        const nextThemes = cont.themes.map((theme) => {
          if (theme.id !== themeId) return theme;
          if (theme.origin === "user") {
            return { ...theme, title: safeTitle || theme.title, updatedAt: t };
          }
          return theme;
        });

        const nextOverrides = { ...(cont.overrides || {}) };
        const nextThemeTitles = { ...(nextOverrides.themeTitles || {}) };
        if (safeTitle) nextThemeTitles[themeId] = safeTitle;else
        delete nextThemeTitles[themeId];

        nextOverrides.themeTitles = nextThemeTitles;

        const nextInit = {
          ...currentInit,
          tagThemes: { ...cont, themes: nextThemes, overrides: nextOverrides, updatedAt: t },
          updatedAt: t
        };

        const nextProj = normalizeProject({
          ...proj,
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: t }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });

      invalidateActiveSnapshotCache();
    },

    createTagTheme(title = "New theme") {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId) return null;

      invalidateActiveSnapshotCache();

      let createdThemeId = null;

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const cont = normalizeTagThemes(currentInit.tagThemes);
        const t = now();
        const safeTitle = safeText(title).trim() || "New theme";
        const baseId = `theme_${safeTitle.toLowerCase().replace(/[^a-z0-9]+/g, "_")}`;
        let id = baseId;
        let n = 2;
        while (cont.themes.some((th) => th.id === id)) {
          id = `${baseId}_${n}`;
          n += 1;
        }
        createdThemeId = id;

        const nextTheme = {
          id,
          title: safeTitle,
          order: cont.themes.length,
          tags: [],
          origin: "user",
          createdAt: t,
          updatedAt: t
        };

        const nextThemes = [...cont.themes, nextTheme];
        const nextInit = {
          ...currentInit,
          tagThemes: { ...cont, themes: nextThemes, updatedAt: t },
          updatedAt: t
        };

        const nextProj = normalizeProject({
          ...proj,
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: t }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });

      invalidateActiveSnapshotCache();
      return createdThemeId;
    },

    deleteTagTheme(themeId) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId || !themeId || themeId === UNASSIGNED_THEME_ID) return;

      invalidateActiveSnapshotCache();

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const cont = normalizeTagThemes(currentInit.tagThemes);
        const t = now();

        const removedTheme = cont.themes.find((th) => th.id === themeId);
        if (!removedTheme) return s;

        const nextThemes = cont.themes.filter((th) => th.id !== themeId).map((th, idx) => ({
          ...th,
          order: idx
        }));

        const nextOverrides = { ...(cont.overrides || {}) };
        const nextThemeTitles = { ...(nextOverrides.themeTitles || {}) };
        delete nextThemeTitles[themeId];

        const nextTagToThemeId = { ...(nextOverrides.tagToThemeId || {}) };
        Object.entries(nextTagToThemeId).forEach(([tag, assigned]) => {
          if (assigned === themeId) nextTagToThemeId[tag] = UNASSIGNED_THEME_ID;
        });

        (removedTheme.tags || []).forEach((tag) => {
          const tKey = safeLowerTag(tag);
          if (tKey) nextTagToThemeId[tKey] = UNASSIGNED_THEME_ID;
        });

        const nextInit = {
          ...currentInit,
          tagThemes: {
            ...cont,
            themes: nextThemes,
            overrides: { tagToThemeId: nextTagToThemeId, themeTitles: nextThemeTitles },
            updatedAt: t
          },
          updatedAt: t
        };

        const nextProj = normalizeProject({
          ...proj,
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: t }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });

      invalidateActiveSnapshotCache();
    },

    moveTagToTheme(tag, themeId) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId) return;

      const tagKey = safeLowerTag(tag);
      const nextThemeId = safeText(themeId).trim();
      if (!tagKey || !nextThemeId) return;

      invalidateActiveSnapshotCache();

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const cont = normalizeTagThemes(currentInit.tagThemes);
        const exists = cont.themes.some((th) => th.id === nextThemeId);
        if (!exists && nextThemeId !== UNASSIGNED_THEME_ID) return s;

        const t = now();
        const nextOverrides = { ...(cont.overrides || {}) };
        const nextTagToThemeId = { ...(nextOverrides.tagToThemeId || {}) };
        nextTagToThemeId[tagKey] = nextThemeId;

        const nextInit = {
          ...currentInit,
          tagThemes: {
            ...cont,
            overrides: { ...nextOverrides, tagToThemeId: nextTagToThemeId },
            updatedAt: t
          },
          updatedAt: t
        };

        const nextProj = normalizeProject({
          ...proj,
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: t }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });

      invalidateActiveSnapshotCache();
    },

    reorderTagThemes(themeIdsInOrder) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId || !Array.isArray(themeIdsInOrder)) return;

      invalidateActiveSnapshotCache();

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const cont = normalizeTagThemes(currentInit.tagThemes);
        const t = now();

        const byId = new Map(cont.themes.map((th) => [th.id, th]));
        const ordered = [];
        themeIdsInOrder.forEach((id) => {
          const theme = byId.get(id);
          if (theme) ordered.push(theme);
        });
        cont.themes.forEach((th) => {
          if (!themeIdsInOrder.includes(th.id)) ordered.push(th);
        });

        const nextThemes = ordered.map((th, idx) => ({ ...th, order: idx }));
        const nextInit = {
          ...currentInit,
          tagThemes: { ...cont, themes: nextThemes, updatedAt: t },
          updatedAt: t
        };

        const nextProj = normalizeProject({
          ...proj,
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: t }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });

      invalidateActiveSnapshotCache();
    },

    ensureTagThemesIntegrity() {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId) return;

      invalidateActiveSnapshotCache();

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const cont = normalizeTagThemes(currentInit.tagThemes);
        const t = now();
        const themeIds = new Set(cont.themes.map((th) => th.id));

        const nextTagToThemeId = {};
        Object.entries(cont.overrides?.tagToThemeId || {}).forEach(([tag, themeId]) => {
          const tagKey = safeLowerTag(tag);
          const targetId = safeText(themeId).trim();
          if (!tagKey || !targetId) return;
          if (targetId !== UNASSIGNED_THEME_ID && !themeIds.has(targetId)) return;
          nextTagToThemeId[tagKey] = targetId;
        });

        const nextThemeTitles = {};
        Object.entries(cont.overrides?.themeTitles || {}).forEach(([themeId, title]) => {
          const id = safeText(themeId).trim();
          if (!id || !themeIds.has(id)) return;
          const safeTitle = safeText(title).trim();
          if (safeTitle) nextThemeTitles[id] = safeTitle;
        });

        const nextInit = {
          ...currentInit,
          tagThemes: {
            ...cont,
            overrides: { tagToThemeId: nextTagToThemeId, themeTitles: nextThemeTitles },
            updatedAt: t
          },
          updatedAt: t
        };

        const nextProj = normalizeProject({
          ...proj,
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: t }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });

      invalidateActiveSnapshotCache();
    },

    updatePersonaContent(personaId, patch) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId) return;

      invalidateActiveSnapshotCache();

      set((s) => {
        const proj = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj) return s;

        const personas = Array.isArray(proj.personas) ? proj.personas : [];
        const idx = personas.findIndex((p) => p.id === personaId);
        if (idx === -1) return s;

        const t = now();
        const current = personas[idx];
        const updated = {
          ...current,
          ...patch,
          isEdited: true,
          isReviewed: false,
          updatedAt: t,
          lastEditedAt: t
        };

        const nextPersonas = personas.slice();
        nextPersonas[idx] = updated;

        const nextProj = normalizeProject({
          ...proj,
          personas: nextPersonas,
          meta: { ...(proj.meta || {}), updatedAt: t }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });
    },

    togglePersonaReviewed(personaId) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId) return;

      invalidateActiveSnapshotCache();

      set((s) => {
        const proj = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj) return s;

        const personas = Array.isArray(proj.personas) ? proj.personas : [];
        const idx = personas.findIndex((p) => p.id === personaId);
        if (idx === -1) return s;

        const t = now();
        const current = personas[idx];
        const nextReviewed = !current.isReviewed;

        const updated = {
          ...current,
          isReviewed: nextReviewed,
          reviewedAt: nextReviewed ? t : null,
          updatedAt: t,
          lastEditedAt: current.lastEditedAt ?? t
        };

        const nextPersonas = personas.slice();
        nextPersonas[idx] = updated;

        const nextProj = normalizeProject({
          ...proj,
          personas: nextPersonas,
          meta: { ...(proj.meta || {}), updatedAt: t }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });
    },

    getInsightGroups() {
      const init = get().getActiveInitiative();
      return normalizeInsightGroups(init?.insightGroups);
    },

    addInsightGroup(title = "Ungrouped insights") {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId) return;

      invalidateActiveSnapshotCache();

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const cont = normalizeInsightGroups(currentInit.insightGroups);

        const t = now();
        const safeTitle = safeText(title).trim() || "Ungrouped insights";
        const id = `grp_${safeTitle.toLowerCase().replace(/[^a-z0-9]+/g, "_")}`;
        const exists = cont.groups.some((g) => g.id === id);

        const groups = exists ?
        cont.groups :
        [...cont.groups, { id, title: safeTitle, order: cont.groups.length, createdAt: t, updatedAt: t }];

        const patchSetGroups = (setContainer) => {
          if (!setContainer || typeof setContainer !== "object") return setContainer;
          const setGroups = normalizeInsightGroups(setContainer.insightGroups);
          const setExists = setGroups.groups.some((g) => g.id === id);
          const patched = setExists ?
          setGroups.groups :
          [...setGroups.groups, { id, title: safeTitle, order: setGroups.groups.length, createdAt: t, updatedAt: t }];
          return {
            ...setContainer,
            insightGroups: { ...setGroups, groups: patched, updatedAt: t },
            updatedAt: t
          };
        };

        const nextInsightsSets = {
          ...(currentInit.insightsSets || {}),
          auto: patchSetGroups(currentInit?.insightsSets?.auto),
          ...(currentInit?.insightsSets?.manual ?
          { manual: patchSetGroups(currentInit.insightsSets.manual) } :
          {})
        };

        let nextInit = {
          ...currentInit,
          insightGroups: { ...cont, groups, updatedAt: t },
          insightsSets: nextInsightsSets,
          updatedAt: t
        };
        nextInit = touchInsightsDraft(nextInit, resolveUserLabel(s), t);
        nextInit = syncActiveInsightsSet(nextInit, t);

        const nextProj = normalizeProject({
          ...proj,
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: t }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });
    },

    renameInsightGroup(groupId, title) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId || !groupId) return;

      invalidateActiveSnapshotCache();

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const cont = normalizeInsightGroups(currentInit.insightGroups);
        const t = now();

        const nextGroups = cont.groups.map((g) =>
        g.id === groupId ? { ...g, title: safeText(title).trim() || g.title, updatedAt: t } : g
        );

        const patchSetGroups = (setContainer) => {
          if (!setContainer || typeof setContainer !== "object") return setContainer;
          const setGroups = normalizeInsightGroups(setContainer.insightGroups);
          const patched = setGroups.groups.map((g) =>
          g.id === groupId ? { ...g, title: safeText(title).trim() || g.title, updatedAt: t } : g
          );
          return {
            ...setContainer,
            insightGroups: { ...setGroups, groups: patched, updatedAt: t },
            updatedAt: t
          };
        };

        const nextInsightsSets = {
          ...(currentInit.insightsSets || {}),
          auto: patchSetGroups(currentInit?.insightsSets?.auto),
          ...(currentInit?.insightsSets?.manual ?
          { manual: patchSetGroups(currentInit.insightsSets.manual) } :
          {})
        };

        let nextInit = {
          ...currentInit,
          insightGroups: { ...cont, groups: nextGroups, updatedAt: t },
          insightsSets: nextInsightsSets,
          updatedAt: t
        };
        nextInit = touchInsightsDraft(nextInit, resolveUserLabel(s), t);
        nextInit = syncActiveInsightsSet(nextInit, t);

        const nextProj = normalizeProject({
          ...proj,
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: t }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });
    },

    deleteInsightGroup(groupId) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId || !groupId || groupId === "grp_ungrouped") return;

      invalidateActiveSnapshotCache();

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const cont = normalizeInsightGroups(currentInit.insightGroups);
        const t = now();

        const nextItems = (currentInit.researchInsights?.items || []).map((it) => {
          if (it.groupId !== groupId) return it;
          return { ...it, groupId: "grp_ungrouped" };
        });

        const nextGroups = cont.groups.
        filter((g) => g.id !== groupId).
        map((g, idx) => ({ ...g, order: idx, updatedAt: t }));

        const synced = syncInsightGroups({ ...cont, groups: nextGroups, updatedAt: t }, nextItems, currentInit.tagThemes);

        let nextInit = {
          ...currentInit,
          insightGroups: synced.groups,
          researchInsights: { ...currentInit.researchInsights, items: synced.items, updatedAt: t },
          updatedAt: t
        };
        nextInit = touchInsightsDraft(nextInit, resolveUserLabel(s), t);
        nextInit = syncActiveInsightsSet(nextInit, t);

        const nextProj = normalizeProject({
          ...proj,
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: t }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });
    },

    reorderInsightGroups(groupIdsInOrder = []) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId) return;

      invalidateActiveSnapshotCache();

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const cont = normalizeInsightGroups(currentInit.insightGroups);
        const t = now();

        const groupById = new Map(cont.groups.map((g) => [g.id, g]));
        const ordered = [];
        groupIdsInOrder.forEach((id) => {
          const group = groupById.get(id);
          if (group) ordered.push(group);
        });
        cont.groups.forEach((g) => {
          if (!groupIdsInOrder.includes(g.id)) ordered.push(g);
        });

        const nextGroups = ordered.map((g, idx) => ({ ...g, order: idx, updatedAt: t }));
        let nextInit = {
          ...currentInit,
          insightGroups: { ...cont, groups: nextGroups, updatedAt: t },
          updatedAt: t
        };
        nextInit = touchInsightsDraft(nextInit, resolveUserLabel(s), t);
        nextInit = syncActiveInsightsSet(nextInit, t);

        const nextProj = normalizeProject({
          ...proj,
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: t }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });
    },

    // ✅ FIXED: actually applies reordering + avoids mutating items in place
    moveInsightToGroup(insightId, groupId, toIndex = null) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId || !insightId || !groupId) return;

      invalidateActiveSnapshotCache();

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const items = Array.isArray(currentInit.researchInsights?.items) ?
        currentInit.researchInsights.items :
        [];

        // First: change groupId
        let nextItems = items.map((it) => it.id === insightId ? { ...it, groupId } : it);

        // Second: optional reorder within group
        if (Number.isFinite(Number(toIndex))) {
          const targetIdx = Math.max(0, Number(toIndex));

          // stable current order within group
          const groupItems = nextItems.
          filter((it) => it.groupId === groupId).
          slice().
          sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));

          const movingIdx = groupItems.findIndex((it) => it.id === insightId);
          if (movingIdx !== -1) {
            const moving = groupItems[movingIdx];
            const others = groupItems.filter((it) => it.id !== insightId);
            const clamped = Math.max(0, Math.min(targetIdx, others.length));
            const reordered = [
            ...others.slice(0, clamped),
            moving,
            ...others.slice(clamped)];


            const orderById = new Map(reordered.map((it, i) => [it.id, i]));

            nextItems = nextItems.map((it) => {
              if (it.groupId !== groupId) return it;
              const o = orderById.get(it.id);
              return o == null ? it : { ...it, order: o };
            });
          }
        }

        const synced = syncInsightGroups(currentInit.insightGroups, nextItems, currentInit.tagThemes);

        const t = now();
        let nextInit = {
          ...currentInit,
          insightGroups: synced.groups,
          researchInsights: { ...currentInit.researchInsights, items: synced.items, updatedAt: t },
          updatedAt: t
        };
        nextInit = touchInsightsDraft(nextInit, resolveUserLabel(s), t);
        nextInit = syncActiveInsightsSet(nextInit, t);

        const nextProj = normalizeProject({
          ...proj,
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: t }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });
    },

    // ✅ FIXED: consistent reorder + no mutation + reliable bounds
    reorderInsightInGroup(groupId, fromIndex, toIndex) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId || !groupId) return;

      invalidateActiveSnapshotCache();

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const items = Array.isArray(currentInit.researchInsights?.items) ?
        currentInit.researchInsights.items :
        [];

        const groupItems = items.
        filter((it) => it.groupId === groupId).
        slice().
        sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));

        const fi = Number(fromIndex);
        const ti = Number(toIndex);
        if (!Number.isFinite(fi) || !Number.isFinite(ti)) return s;
        if (fi < 0 || fi >= groupItems.length || ti < 0 || ti >= groupItems.length) return s;
        if (fi === ti) return s;

        const moving = groupItems[fi];
        const others = groupItems.filter((_, idx) => idx !== fi);
        others.splice(ti, 0, moving);

        const orderById = new Map(others.map((it, i) => [it.id, i]));

        const nextItems = items.map((it) => {
          if (it.groupId !== groupId) return it;
          const o = orderById.get(it.id);
          return o == null ? it : { ...it, order: o };
        });

        const synced = syncInsightGroups(currentInit.insightGroups, nextItems, currentInit.tagThemes);

        const t = now();
        let nextInit = {
          ...currentInit,
          insightGroups: synced.groups,
          researchInsights: { ...currentInit.researchInsights, items: synced.items, updatedAt: t },
          updatedAt: t
        };
        nextInit = touchInsightsDraft(nextInit, resolveUserLabel(s), t);
        nextInit = syncActiveInsightsSet(nextInit, t);

        const nextProj = normalizeProject({
          ...proj,
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: t }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });
    },

    markInsightsUsed() {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId) return;

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const t = now();
        const nextInit = { ...currentInit, insightsUsedAt: t, updatedAt: t };

        const nextProj = normalizeProject({
          ...proj,
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: t }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });

      invalidateActiveSnapshotCache();
    },

    addInsight(payload) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId || !payload) return;

      invalidateActiveSnapshotCache();

      const insightId = uid("ins");
      const tIso = isoNow();
      let didAdd = false;

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const researchItems = currentInit.researchItems || [];
        const researchItemsById = new Map(researchItems.map((ri) => [ri.id, ri]));
        const t = now();

        const rawEvidence = normalizeEvidenceItems(payload.evidence, researchItemsById);
        const evidence = filterEvidenceForResearch(rawEvidence, researchItems.length);
        const evidenceStrength = computeEvidenceStrength(evidence);
        const needsEvidence = computeNeedsEvidence(evidence);

        const newInsight = {
          id: insightId,
          text: safeText(payload.text).trim(),
          tags: Array.isArray(payload.tags) ? payload.tags : [],
          groupId: safeText(payload.groupId || "grp_ungrouped").trim() || "grp_ungrouped",
          order: Number.isFinite(Number(payload.order)) ?
          Number(payload.order) :
          currentInit.researchInsights?.items?.length || 0,
          reviewStatus: "edited",
          provenance: { createdBy: "human", lastEditedBy: "human", createdAt: t, updatedAt: t },
          createdBy: "local-user",
          updatedBy: "local-user",
          updatedAt: tIso,
          needsEvidence,
          evidenceStrength,
          evidence
        };

        if (!newInsight.text) return s;

        const items = [...(currentInit.researchInsights?.items || []), newInsight];
        const synced = syncInsightGroups(currentInit.insightGroups, items, currentInit.tagThemes);
        didAdd = true;

        let nextInit = {
          ...currentInit,
          insightGroups: synced.groups,
          researchInsights: resetInsightsReviewStatusIfEdited(
            { ...currentInit.researchInsights, items: synced.items, updatedAt: t },
            true
          ),
          insightsEditedAt: t,
          insightsEditedBy: resolveUserLabel(s),
          updatedAt: t
        };
        nextInit = syncActiveInsightsSet(nextInit, t);
        nextInit = markInsightsApprovalStaleIfNeeded(nextInit, true);
        nextInit = markInsightsDerived(nextInit, getResearchVersion(nextInit));

        const activityEntry = makeActivityEntry({
          entityType: "insight",
          entityId: insightId,
          initiativeId: initId,
          action: "created",
          actor: "user",
          timestamp: tIso,
          shortDescription: "Insight created."
        });

        const nextProj = normalizeProject({
          ...appendActivity(proj, activityEntry),
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: t }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });

      return didAdd ? insightId : null;
    },

    updateInsight(insightId, patch) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId || !insightId) return;

      invalidateActiveSnapshotCache();

      const tIso = isoNow();

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const researchItems = currentInit.researchItems || [];
        const researchItemsById = new Map(researchItems.map((ri) => [ri.id, ri]));
        const t = now();

        let didEdit = false;
        let editActor = "user";

        const nextItems = (currentInit.researchInsights?.items || []).map((it) => {
          if (it.id !== insightId) return it;

          const nextText = typeof patch?.text === "string" ? patch.text.trim() : it.text;
          const nextTags = Array.isArray(patch?.tags) ? patch.tags : it.tags;
          const nextGroupId =
          typeof patch?.groupId === "string" && patch.groupId.trim() ? patch.groupId.trim() : it.groupId;
          const explicitReviewStatus = typeof patch?.reviewStatus === "string" ? patch.reviewStatus : null;
          const explicitLastEditedBy =
          patch?.lastEditedBy === "human" || patch?.lastEditedBy === "ai" ? patch.lastEditedBy : null;
          const patchUpdatedBy =
          typeof patch?.updatedBy === "string" && patch.updatedBy.trim() ? patch.updatedBy.trim() : null;
          const confidenceOverride =
          typeof patch?.confidenceOverride === "string" ?
          patch.confidenceOverride :
          patch?.confidenceOverride === null ?
          null :
          it.confidenceOverride;

          const rawEvidence = Object.prototype.hasOwnProperty.call(patch || {}, "evidence") ?
          normalizeEvidenceItems(patch?.evidence, researchItemsById) :
          it.evidence;

          const evidence = filterEvidenceForResearch(rawEvidence, researchItems.length);

          const evidenceStrength = computeEvidenceStrength(evidence);
          const needsEvidence =
          typeof patch?.needsEvidence === "boolean" ? patch.needsEvidence : computeNeedsEvidence(evidence);

          const evidenceChanged = JSON.stringify(evidence) !== JSON.stringify(it.evidence);
          const edited =
          nextText !== it.text || JSON.stringify(nextTags) !== JSON.stringify(it.tags) || nextGroupId !== it.groupId;
          const reviewStatusNext = explicitReviewStatus || (edited ? "edited" : it.reviewStatus);
          const reviewStatusChanged = reviewStatusNext !== it.reviewStatus;
          const confidenceChanged = confidenceOverride !== it.confidenceOverride;
          const needsEvidenceChanged = needsEvidence !== it.needsEvidence;
          const didChange = edited || evidenceChanged || reviewStatusChanged || needsEvidenceChanged || confidenceChanged;
          const isAiUpdate = patchUpdatedBy === "ai" || explicitLastEditedBy === "ai";

          if (didChange) {
            didEdit = true;
            editActor = isAiUpdate ? "ai" : "user";
          }

          const createdBy =
          typeof it.createdBy === "string" && it.createdBy.trim() ? it.createdBy.trim() : "local-user";
          const updatedBy = didChange ? isAiUpdate ? "ai" : "local-user" : it.updatedBy || createdBy;
          const updatedAt = didChange ? tIso : it.updatedAt;

          return {
            ...it,
            text: nextText,
            tags: nextTags,
            groupId: nextGroupId,
            evidence,
            evidenceStrength,
            needsEvidence,
            confidenceOverride,
            reviewStatus: reviewStatusNext,
            provenance: {
              ...it.provenance,
              lastEditedBy: explicitLastEditedBy || (edited ? "human" : it.provenance?.lastEditedBy || "ai"),
              updatedAt: t
            },
            createdBy,
            updatedBy,
            ...(updatedAt ? { updatedAt } : {})
          };
        });

        const synced = syncInsightGroups(currentInit.insightGroups, nextItems, currentInit.tagThemes);
        const decisionSync = reconcileDecisionsWithInsights(
          currentInit.decisions,
          getDecisionInsights(currentInit),
          initId
        );

        let nextInit = {
          ...currentInit,
          insightGroups: synced.groups,
          researchInsights: resetInsightsReviewStatusIfEdited(
            { ...currentInit.researchInsights, items: synced.items, updatedAt: t },
            didEdit
          ),
          ...(didEdit ?
          {
            insightsEditedAt: t,
            insightsEditedBy: editActor === "ai" ? "AI" : resolveUserLabel(s)
          } :
          {}),
          decisions: decisionSync.decisions,
          updatedAt: t
        };
        nextInit = syncActiveInsightsSet(nextInit, t);
        nextInit = markInsightsApprovalStaleIfNeeded(nextInit, didEdit);
        if (didEdit) {
          nextInit = markInsightsDerived(nextInit, getResearchVersion(nextInit));
        }

        const activityEntries = [];
        if (didEdit) {
          activityEntries.push(
            makeActivityEntry({
              entityType: "insight",
              entityId: insightId,
              initiativeId: initId,
              action: "edited",
              actor: editActor,
              timestamp: tIso,
              shortDescription: "Insight edited."
            })
          );
        }
        activityEntries.push(...(decisionSync.activityEntries || []));

        const nextProj = normalizeProject({
          ...appendActivity(proj, activityEntries.filter(Boolean)),
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: t }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });
    },

    deleteInsight(insightId) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId || !insightId) return;

      invalidateActiveSnapshotCache();

      const tIso = isoNow();

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const nextItems = (currentInit.researchInsights?.items || []).filter((it) => it.id !== insightId);
        const synced = syncInsightGroups(currentInit.insightGroups, nextItems, currentInit.tagThemes);
        const decisionSync = reconcileDecisionsWithInsights(
          currentInit.decisions,
          getDecisionInsights(currentInit),
          initId
        );

        const t = now();
        let nextInit = {
          ...currentInit,
          insightGroups: synced.groups,
          researchInsights: resetInsightsReviewStatusIfEdited(
            { ...currentInit.researchInsights, items: synced.items, updatedAt: t },
            true
          ),
          insightsEditedAt: t,
          insightsEditedBy: resolveUserLabel(s),
          decisions: decisionSync.decisions,
          updatedAt: t
        };
        nextInit = syncActiveInsightsSet(nextInit, t);
        nextInit = markInsightsApprovalStaleIfNeeded(nextInit, true);
        nextInit = markInsightsDerived(nextInit, getResearchVersion(nextInit));

        const activityEntries = [
        makeActivityEntry({
          entityType: "insight",
          entityId: insightId,
          initiativeId: initId,
          action: "deleted",
          actor: "user",
          timestamp: tIso,
          shortDescription: "Insight deleted."
        }),
        ...(decisionSync.activityEntries || [])];


        const nextProj = normalizeProject({
          ...appendActivity(proj, activityEntries.filter(Boolean)),
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: t }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });
    },

    markInsightReviewed(insightId, reviewed = true) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId || !insightId) return;

      invalidateActiveSnapshotCache();

      const tIso = isoNow();

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const t = now();

        const nextItems = (currentInit.researchInsights?.items || []).map((it) => {
          if (it.id !== insightId) return it;
          return {
            ...it,
            reviewStatus: reviewed ? "reviewed" : it.reviewStatus,
            provenance: { ...it.provenance, lastEditedBy: "human", updatedAt: t },
            updatedBy: "local-user",
            updatedAt: tIso
          };
        });

        const decisionSync = reconcileDecisionsWithInsights(
          currentInit.decisions,
          getDecisionInsights(currentInit),
          initId
        );

        let nextInit = {
          ...currentInit,
          researchInsights: resetInsightsReviewStatusIfEdited(
            { ...currentInit.researchInsights, items: nextItems, updatedAt: t },
            true
          ),
          insightsEditedAt: t,
          insightsEditedBy: resolveUserLabel(s),
          decisions: decisionSync.decisions,
          updatedAt: t
        };
        nextInit = syncActiveInsightsSet(nextInit, t);
        nextInit = markInsightsApprovalStaleIfNeeded(nextInit, true);
        nextInit = markInsightsDerived(nextInit, getResearchVersion(nextInit));

        const activityEntries = [];
        if (reviewed) {
          activityEntries.push(
            makeActivityEntry({
              entityType: "insight",
              entityId: insightId,
              initiativeId: initId,
              action: "approved",
              actor: "user",
              timestamp: tIso,
              shortDescription: "Insight approved."
            })
          );
        }
        activityEntries.push(...(decisionSync.activityEntries || []));

        const nextProj = normalizeProject({
          ...appendActivity(proj, activityEntries.filter(Boolean)),
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: t }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });
    },

    approveInsightsSnapshot({ snapshot, snapshotHash }) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId) return;

      invalidateActiveSnapshotCache();

      set((s) => {
        const approver = resolveUserLabel(s);
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const t = now();
        const incomingSnapshotHash = safeText(snapshotHash).trim();
        const previousSnapshotHash = safeText(currentInit?.insightsApprovedSnapshotHash).trim();
        const alreadyApproved = safeText(currentInit?.insightsApprovalStatus).trim().toLowerCase() === "approved";
        const isSameApproval =
          alreadyApproved &&
          Boolean(incomingSnapshotHash) &&
          Boolean(previousSnapshotHash) &&
          incomingSnapshotHash === previousSnapshotHash;

        const nextInsights = {
          ...currentInit.researchInsights,
          reviewStatus: "reviewed",
          reviewedAt: isSameApproval ? currentInit?.researchInsights?.reviewedAt ?? t : t,
          updatedAt: isSameApproval ? currentInit?.researchInsights?.updatedAt ?? t : t
        };

        let nextInit = {
          ...currentInit,
          researchInsights: nextInsights,
          insightsApprovalStatus: "approved",
          insightsApprovedSnapshot: snapshot || null,
          insightsApprovedSnapshotHash: incomingSnapshotHash || "",
          insightsApprovedAt: isSameApproval ? Number(currentInit?.insightsApprovedAt || t) : t,
          insightsApprovedBy: isSameApproval ? safeText(currentInit?.insightsApprovedBy).trim() || approver : approver,
          insightsVersion: isSameApproval
            ? (Number.isFinite(Number(currentInit?.insightsVersion)) ? Number(currentInit.insightsVersion) : 0)
            : bumpVersion(currentInit?.insightsVersion, 0),
          insightsEditedAt: isSameApproval ? Number(currentInit?.insightsEditedAt || currentInit?.insightsApprovedAt || t) : t,
          insightsEditedBy: isSameApproval ? safeText(currentInit?.insightsEditedBy).trim() || approver : approver,
          updatedAt: t
        };
        nextInit = syncActiveInsightsSet(nextInit, t);

        const nextProj = normalizeProject({
          ...proj,
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: t }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });
    },

    addInsightEvidence(insightId, payload) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId || !insightId) return;

      invalidateActiveSnapshotCache();

      const tIso = isoNow();

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const researchItemsById = new Map((currentInit.researchItems || []).map((ri) => [ri.id, ri]));
        const t = now();

        const baseEvidence = normalizeEvidenceItems(
          [
          {
            id: typeof payload?.id === "string" && payload.id.trim() ? payload.id.trim() : uid("ev"),
            text: safeText(payload?.text).trim(),
            type: payload?.type,
            researchItemId: payload?.researchItemId,
            start: payload?.start,
            end: payload?.end
          }],

          researchItemsById
        );

        if (!baseEvidence.length) return s;

        const nextItems = (currentInit.researchInsights?.items || []).map((it) => {
          if (it.id !== insightId) return it;

          const merged = [...(Array.isArray(it.evidence) ? it.evidence : []), ...baseEvidence];
          const nextEvidence = filterEvidenceForResearch(merged, (currentInit.researchItems || []).length);
          const evidenceStrength = computeEvidenceStrength(nextEvidence);
          const needsEvidence = computeNeedsEvidence(nextEvidence);

          return {
            ...it,
            evidence: nextEvidence,
            evidenceStrength,
            needsEvidence,
            provenance: { ...it.provenance, lastEditedBy: "human", updatedAt: t },
            updatedBy: "local-user",
            updatedAt: tIso
          };
        });

        let nextInit = {
          ...currentInit,
          researchInsights: resetInsightsReviewStatusIfEdited(
            { ...currentInit.researchInsights, items: nextItems, updatedAt: t },
            true
          ),
          insightsEditedAt: t,
          insightsEditedBy: resolveUserLabel(s),
          updatedAt: t
        };
        nextInit = syncActiveInsightsSet(nextInit, t);
        nextInit = markInsightsApprovalStaleIfNeeded(nextInit, true);
        nextInit = markInsightsDerived(nextInit, getResearchVersion(nextInit));

        const activityEntry = makeActivityEntry({
          entityType: "insight",
          entityId: insightId,
          initiativeId: initId,
          action: "edited",
          actor: "user",
          timestamp: tIso,
          shortDescription: "Insight evidence added."
        });

        const nextProj = normalizeProject({
          ...appendActivity(proj, activityEntry),
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: t }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });
    },

    updateInsightEvidence(insightId, evidenceId, patch) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId || !insightId || !evidenceId) return;

      invalidateActiveSnapshotCache();

      const tIso = isoNow();

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const researchItemsById = new Map((currentInit.researchItems || []).map((ri) => [ri.id, ri]));
        const t = now();

        const nextItems = (currentInit.researchInsights?.items || []).map((it) => {
          if (it.id !== insightId) return it;

          const rawEvidence = (Array.isArray(it.evidence) ? it.evidence : []).map((ev) => {
            if (ev.id !== evidenceId) return ev;
            const next = { ...ev, ...patch };
            return normalizeEvidenceItems([next], researchItemsById)[0] || ev;
          });

          const nextEvidence = filterEvidenceForResearch(rawEvidence, (currentInit.researchItems || []).length);
          const evidenceStrength = computeEvidenceStrength(nextEvidence);
          const needsEvidence = computeNeedsEvidence(nextEvidence);

          return {
            ...it,
            evidence: nextEvidence,
            evidenceStrength,
            needsEvidence,
            provenance: { ...it.provenance, lastEditedBy: "human", updatedAt: t },
            updatedBy: "local-user",
            updatedAt: tIso
          };
        });

        let nextInit = {
          ...currentInit,
          researchInsights: resetInsightsReviewStatusIfEdited(
            { ...currentInit.researchInsights, items: nextItems, updatedAt: t },
            true
          ),
          insightsEditedAt: t,
          insightsEditedBy: resolveUserLabel(s),
          updatedAt: t
        };
        nextInit = syncActiveInsightsSet(nextInit, t);
        nextInit = markInsightsApprovalStaleIfNeeded(nextInit, true);
        nextInit = markInsightsDerived(nextInit, getResearchVersion(nextInit));

        const activityEntry = makeActivityEntry({
          entityType: "insight",
          entityId: insightId,
          initiativeId: initId,
          action: "edited",
          actor: "user",
          timestamp: tIso,
          shortDescription: "Insight evidence updated."
        });

        const nextProj = normalizeProject({
          ...appendActivity(proj, activityEntry),
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: t }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });
    },

    deleteInsightEvidence(insightId, evidenceId) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId || !insightId || !evidenceId) return;

      invalidateActiveSnapshotCache();

      const tIso = isoNow();

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const t = now();

        const nextItems = (currentInit.researchInsights?.items || []).map((it) => {
          if (it.id !== insightId) return it;

          const rawEvidence = (Array.isArray(it.evidence) ? it.evidence : []).filter((ev) => ev.id !== evidenceId);
          const nextEvidence = filterEvidenceForResearch(rawEvidence, (currentInit.researchItems || []).length);
          const evidenceStrength = computeEvidenceStrength(nextEvidence);
          const needsEvidence = computeNeedsEvidence(nextEvidence);

          return {
            ...it,
            evidence: nextEvidence,
            evidenceStrength,
            needsEvidence,
            provenance: { ...it.provenance, lastEditedBy: "human", updatedAt: t },
            updatedBy: "local-user",
            updatedAt: tIso
          };
        });

        let nextInit = {
          ...currentInit,
          researchInsights: resetInsightsReviewStatusIfEdited(
            { ...currentInit.researchInsights, items: nextItems, updatedAt: t },
            true
          ),
          insightsEditedAt: t,
          insightsEditedBy: resolveUserLabel(s),
          updatedAt: t
        };
        nextInit = syncActiveInsightsSet(nextInit, t);
        nextInit = markInsightsApprovalStaleIfNeeded(nextInit, true);
        nextInit = markInsightsDerived(nextInit, getResearchVersion(nextInit));

        const activityEntry = makeActivityEntry({
          entityType: "insight",
          entityId: insightId,
          initiativeId: initId,
          action: "edited",
          actor: "user",
          timestamp: tIso,
          shortDescription: "Insight evidence removed."
        });

        const nextProj = normalizeProject({
          ...appendActivity(proj, activityEntry),
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: t }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });
    },

    setDecisions(decisions = []) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId) return;

      invalidateActiveSnapshotCache();

      const tIso = isoNow();

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];

        const incoming = Array.isArray(decisions) ? decisions : [];
        try {
          void 0;




        } catch {}
        const prepared = incoming.
        map((decision) => {
          const raw = decision && typeof decision === "object" ? decision : {};
          const title = safeText(raw.title).trim();
          if (!title) return null;
          const committedInsightsVersion = Number.isFinite(Number(raw.committedInsightsVersion)) ?
          Number(raw.committedInsightsVersion) :
          null;
          const staleFromInsights = raw?.staleFromInsights === true;

          return {
            id: safeText(raw.id).trim() || uid("dec"),
            title,
            description: safeText(raw.description).trim(),
            projectId:
            typeof raw.projectId === "string" && raw.projectId.trim() ?
            raw.projectId.trim() :
            activeProjectId,
            initiativeId:
            typeof raw.initiativeId === "string" && raw.initiativeId.trim() ?
            raw.initiativeId.trim() :
            initId,
            linkedInsightIds: Array.isArray(raw.linkedInsightIds) ?
            raw.linkedInsightIds :
            Array.isArray(raw.insightIds) ?
            raw.insightIds :
            Array.isArray(raw.relatedInsightIds) ?
            raw.relatedInsightIds :
            [],
            linkedResearchItemIds: Array.isArray(raw.linkedResearchItemIds) ? raw.linkedResearchItemIds : [],
            evidence: Array.isArray(raw.evidence) ? raw.evidence : [],
            status: typeof raw.status === "string" ? raw.status : "draft",
            committedAt: safeText(raw.committedAt).trim() || null,
            committedBy: safeText(raw.committedBy).trim() || null,
            committedInsightsVersion,
            committedInsightIds: Array.isArray(raw.committedInsightIds) ? raw.committedInsightIds : [],
            ...(raw?.confirmationSnapshot && typeof raw.confirmationSnapshot === "object" ?
            { confirmationSnapshot: raw.confirmationSnapshot } :
            {}),
            ...(staleFromInsights ? { staleFromInsights: true } : {}),
            ...(safeText(raw?.staleReason).trim() ? { staleReason: safeText(raw.staleReason).trim() } : {}),
            ...(safeText(raw?.staleAt).trim() ? { staleAt: safeText(raw.staleAt).trim() } : {}),
            createdBy: safeText(raw.createdBy).trim() || "local-user",
            updatedBy: safeText(raw.updatedBy).trim() || "local-user",
            createdAt: safeText(raw.createdAt).trim() || tIso,
            updatedAt: safeText(raw.updatedAt).trim() || tIso
          };
        }).
        filter(Boolean);

        logDecisionEvidenceDebug("setDecisions.incoming", {
          count: prepared.length,
          sample: prepared.slice(0, 3).map((decision) => ({
            id: decision.id,
            title: decision.title,
            status: decision.status,
            updatedAt: decision.updatedAt,
            createdAt: decision.createdAt,
            linkedInsightIds: safeArray(decision.linkedInsightIds).length,
            linkedResearchItemIds: safeArray(decision.linkedResearchItemIds).length,
            evidence: safeArray(decision.evidence).length
          }))
        });

        let nextDecisions = [];
        try {
          nextDecisions = normalizeDecisions(prepared, getDecisionInsights(currentInit));
          try {
            void 0;


          } catch {}
        } catch (error) {
          void 0;
          nextDecisions = prepared;
        }

        const t = now();
        const nextInit = {
          ...currentInit,
          decisions: nextDecisions,
          updatedAt: t
        };

        let nextProj;
        try {
          nextProj = normalizeProject({
            ...proj,
            initiatives: {
              ...proj.initiatives,
              byId: { ...proj.initiatives.byId, [initId]: nextInit },
              activeId: initId
            },
            meta: { ...(proj.meta || {}), updatedAt: t }
          });
        } catch (error) {
          void 0;
          nextProj = {
            ...proj,
            initiatives: {
              ...proj.initiatives,
              byId: { ...proj.initiatives.byId, [initId]: nextInit },
              activeId: initId
            },
            meta: { ...(proj.meta || {}), updatedAt: t }
          };
        }

        try {
          void 0;










        } catch {}

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });
    },

    createDecision({
      title,
      description,
      linkedInsightIds,
      linkedResearchItemIds,
      initiativeId
    } = {}) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId) return;

      invalidateActiveSnapshotCache();

      const tIso = isoNow();
      const decisionId = uid("dec");

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const candidateInitId =
        typeof initiativeId === "string" && initiativeId.trim() ? initiativeId.trim() : null;
        const targetInitId = candidateInitId && proj?.initiatives?.byId?.[candidateInitId] ?
        candidateInitId :
        initId;
        const decisionInitId = candidateInitId && proj?.initiatives?.byId?.[candidateInitId] ?
        candidateInitId :
        null;

        const currentInit = proj.initiatives.byId[targetInitId];
        const insightsById = new Map(
          getDecisionInsights(currentInit).map((insight) => [insight.id, insight])
        );

        const titleText = safeText(title).trim();
        if (!titleText) return s;

        const rawLinks = Array.isArray(linkedInsightIds) ? linkedInsightIds : [];
        const normalizedLinks = rawLinks.map((id) => typeof id === "string" ? id.trim() : "").filter(Boolean);
        const approvedLinks = insightsById.size ?
        normalizedLinks.filter((id) => {
          const insight = insightsById.get(id);
          if (!insight) return true;
          return isInsightApproved(insight);
        }) :
        normalizedLinks;
        const normalizedResearchLinks = normalizeStringArray(linkedResearchItemIds);

        const newDecision = {
          id: decisionId,
          title: titleText,
          description: safeText(description).trim(),
          projectId: activeProjectId,
          initiativeId: decisionInitId,
          linkedInsightIds: approvedLinks,
          linkedResearchItemIds: normalizedResearchLinks,
          status: "draft",
          createdBy: "local-user",
          updatedBy: "local-user",
          createdAt: tIso,
          updatedAt: tIso
        };

        const nextDecisions = normalizeDecisions(
          [...(currentInit.decisions || []), newDecision],
          getDecisionInsights(currentInit)
        );

        const t = now();
        const nextInit = {
          ...currentInit,
          decisions: nextDecisions,
          updatedAt: t
        };

        const activityEntry = makeActivityEntry({
          entityType: "decision",
          entityId: decisionId,
          initiativeId: decisionInitId ?? null,
          action: "created",
          actor: "user",
          timestamp: tIso,
          shortDescription: "Decision created."
        });

        const nextProj = normalizeProject({
          ...appendActivity(proj, activityEntry),
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [targetInitId]: nextInit },
            activeId: targetInitId
          },
          meta: { ...(proj.meta || {}), updatedAt: t }
        });

        return {
          projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } },
          activeInitiativeByProjectId: {
            ...(s.activeInitiativeByProjectId || {}),
            [activeProjectId]: targetInitId
          }
        };
      });

      return decisionId;
    },

    updateDecision(decisionId, patch) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId || !decisionId) return;

      invalidateActiveSnapshotCache();

      const tIso = isoNow();

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const insightsById = new Map(
          (currentInit.researchInsights?.items || []).map((insight) => [insight.id, insight])
        );
        const hasDeliveryPatch = patch && Object.prototype.hasOwnProperty.call(patch, "deliveryStories");
        const hasLinkedInsightPatch = patch && Object.prototype.hasOwnProperty.call(patch, "linkedInsightIds");
        const hasLinkedResearchPatch =
        patch && Object.prototype.hasOwnProperty.call(patch, "linkedResearchItemIds");

        let didEdit = false;

        const nextDecisions = (currentInit.decisions || []).map((dec) => {
          if (dec.id !== decisionId) return dec;

          const nextTitle =
          typeof patch?.title === "string" && patch.title.trim() ? patch.title.trim() : dec.title;
          const nextDescription =
          typeof patch?.description === "string" ? patch.description.trim() : dec.description;
          const nextStatusRaw = typeof patch?.status === "string" ? patch.status.trim() : dec.status;
          let nextStatus = DECISION_STATUS_SET.has(nextStatusRaw) ? nextStatusRaw : "draft";

          const rawLinks = hasLinkedInsightPatch ?
          Array.isArray(patch?.linkedInsightIds) ?
          patch.linkedInsightIds :
          [] :
          dec.linkedInsightIds;
          const filteredLinks = (Array.isArray(rawLinks) ? rawLinks : []).
          map((id) => typeof id === "string" ? id.trim() : "").
          filter((id) => {
            const insight = insightsById.get(id);
            if (!id) return false;
            if (!insight) return true;
            return isInsightApproved(insight);
          });
          const nextResearchLinks = hasLinkedResearchPatch ?
          normalizeStringArray(Array.isArray(patch?.linkedResearchItemIds) ? patch.linkedResearchItemIds : []) :
          dec.linkedResearchItemIds || [];
          const nextDeliveryStories = hasDeliveryPatch ?
          normalizeDecisionDeliveryStories(patch?.deliveryStories, dec.id, nextDescription) :
          dec.deliveryStories;

          const linksChanged =
          hasLinkedInsightPatch &&
          JSON.stringify(filteredLinks) !== JSON.stringify(dec.linkedInsightIds || []);
          const researchLinksChanged =
          JSON.stringify(nextResearchLinks) !== JSON.stringify(dec.linkedResearchItemIds || []);
          const deliveryChanged =
          hasDeliveryPatch &&
          JSON.stringify(nextDeliveryStories || []) !== JSON.stringify(dec.deliveryStories || []);
          if (linksChanged) nextStatus = "revisit";

          const didChange =
          nextTitle !== dec.title ||
          nextDescription !== dec.description ||
          nextStatus !== dec.status ||
          linksChanged ||
          researchLinksChanged ||
          deliveryChanged;

          if (!didChange) return dec;
          didEdit = true;

          const committing = nextStatus === "committed" && dec.status !== "committed";
          const shouldPersistInsightSnapshot = filteredLinks.length > 0;
          const confirmationSnapshot = committing ?
          buildDecisionConfirmationSnapshot({
            decision: {
              ...dec,
              title: nextTitle,
              description: nextDescription,
              linkedInsightIds: filteredLinks
            },
            currentInit,
            confirmedAt: tIso,
            confirmedBy: "local-user"
          }) :
          null;

          return {
            ...dec,
            title: nextTitle,
            description: nextDescription,
            linkedInsightIds: filteredLinks,
            linkedResearchItemIds: nextResearchLinks,
            ...(hasDeliveryPatch ? { deliveryStories: nextDeliveryStories } : {}),
            status: nextStatus,
            ...(committing ?
            {
              committedAt: tIso,
              committedBy: "local-user",
              committedInsightsVersion:
              shouldPersistInsightSnapshot && Number.isFinite(Number(currentInit?.insightsVersion)) ?
              Number(currentInit.insightsVersion) :
              null,
              committedInsightIds: shouldPersistInsightSnapshot ? filteredLinks : [],
              confirmationSnapshot,
              staleFromInsights: false,
              staleReason: null,
              staleAt: null
            } :
            {}),
            updatedBy: "local-user",
            updatedAt: tIso
          };
        });

        if (!didEdit) return s;

        const t = now();
        const nextInit = {
          ...currentInit,
          decisions: nextDecisions,
          updatedAt: t
        };

        const activityEntry = makeActivityEntry({
          entityType: "decision",
          entityId: decisionId,
          initiativeId: initId,
          action: "edited",
          actor: "user",
          timestamp: tIso,
          shortDescription: "Decision edited."
        });

        const nextProj = normalizeProject({
          ...appendActivity(proj, activityEntry),
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: t }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });
    },

    deleteDecision(decisionId) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId || !decisionId) return;

      invalidateActiveSnapshotCache();

      const tIso = isoNow();

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const nextDecisions = (currentInit.decisions || []).filter((dec) => dec.id !== decisionId);

        const t = now();
        const nextInit = {
          ...currentInit,
          decisions: nextDecisions,
          updatedAt: t
        };

        const activityEntry = makeActivityEntry({
          entityType: "decision",
          entityId: decisionId,
          initiativeId: initId,
          action: "deleted",
          actor: "user",
          timestamp: tIso,
          shortDescription: "Decision deleted."
        });

        const nextProj = normalizeProject({
          ...appendActivity(proj, activityEntry),
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: t }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });
    },

    setActiveInsightsSet(mode = "auto") {
      const nextMode = mode === "manual" ? "manual" : "auto";
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId) return;

      invalidateActiveSnapshotCache();

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;

        const currentInit = proj.initiatives.byId[initId];
        const sets = currentInit.insightsSets && typeof currentInit.insightsSets === "object" ? currentInit.insightsSets : {};
        const targetSet = nextMode === "manual" ? sets.manual : sets.auto;
        if (!targetSet || !targetSet.researchInsights || !targetSet.insightGroups) return s;

        let nextInit = {
          ...currentInit,
          activeInsightsSet: nextMode,
          researchInsights: normalizeInsightsContainer(targetSet.researchInsights, currentInit.researchItems),
          insightGroups: normalizeInsightGroups(targetSet.insightGroups),
          updatedAt: now()
        };
        nextInit = touchInsightsDraft(nextInit, resolveUserLabel(s));

        const nextProj = normalizeProject({
          ...proj,
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [initId]: nextInit },
            activeId: initId
          },
          meta: { ...(proj.meta || {}), updatedAt: now() }
        });

        return { projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } } };
      });
    },


    markDecisionCommitted(decisionId) {
      const { activeProjectId } = get().selectActiveRefs();
      if (!activeProjectId || !decisionId) return;

      invalidateActiveSnapshotCache();

      const tIso = isoNow();

      const targetDecisionId = safeText(decisionId).trim();

      set((s) => {
        const proj0 = normalizeProject(s.projects.byId[activeProjectId]);
        if (!proj0) return s;

        const mappedInitId = (s.activeInitiativeByProjectId || {})[activeProjectId] || null;
        const { project: proj, initId } = ensureActiveInitiativeForProject(proj0, mappedInitId);
        if (!initId) return s;
        const targetInitId =
        (proj?.initiatives?.allIds || []).find((candidateId) =>
        safeArray(proj?.initiatives?.byId?.[candidateId]?.decisions).some(
          (decision) => safeText(decision?.id).trim() === targetDecisionId
        )
        ) || initId;
        const currentInit = proj.initiatives.byId[targetInitId];
        let didChange = false;

        const nextDecisions = (currentInit.decisions || []).map((dec) => {
          if (safeText(dec?.id).trim() !== targetDecisionId) return dec;
          if (dec.status === "committed") return dec;
          const linkedInsightIds = Array.isArray(dec?.linkedInsightIds) ? dec.linkedInsightIds : [];
          const shouldPersistInsightSnapshot = linkedInsightIds.length > 0;
          const confirmationSnapshot = buildDecisionConfirmationSnapshot({
            decision: dec,
            currentInit,
            confirmedAt: tIso,
            confirmedBy: "local-user"
          });
          didChange = true;
          return {
            ...dec,
            status: "committed",
            committedAt: tIso,
            committedBy: "local-user",
            committedInsightsVersion:
            shouldPersistInsightSnapshot && Number.isFinite(Number(currentInit?.insightsVersion)) ?
            Number(currentInit.insightsVersion) :
            null,
            committedInsightIds: shouldPersistInsightSnapshot ? linkedInsightIds : [],
            confirmationSnapshot,
            staleFromInsights: false,
            staleReason: null,
            staleAt: null,
            updatedBy: "local-user",
            updatedAt: tIso
          };
        });

        if (!didChange) return s;

        const t = now();
        const nextInit = {
          ...currentInit,
          decisions: nextDecisions,
          updatedAt: t
        };

        const activityEntry = makeActivityEntry({
          entityType: "decision",
          entityId: targetDecisionId,
          initiativeId: targetInitId,
          action: "committed",
          actor: "user",
          timestamp: tIso,
          shortDescription: "Decision committed."
        });

        const nextProj = normalizeProject({
          ...appendActivity(proj, activityEntry),
          initiatives: {
            ...proj.initiatives,
            byId: { ...proj.initiatives.byId, [targetInitId]: nextInit },
            activeId: targetInitId
          },
          meta: { ...(proj.meta || {}), updatedAt: t }
        });

        return {
          projects: { ...s.projects, byId: { ...s.projects.byId, [activeProjectId]: nextProj } },
          activeInitiativeByProjectId: {
            ...(s.activeInitiativeByProjectId || {}),
            [activeProjectId]: targetInitId
          }
        };
      });
    }
  })
);
